<template>
    <div class="meeting-page" ref="pageRoot">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Team</div>
                <div class="page-bar-main">Meetings</div>
            </div>
        </div>

        <div v-if="!$store.state.user.teamHubEmployee" class="meeting-capture-saved" role="alert">
            <strong>Your login needs a linked employee profile to use Meetings.</strong>
            <p>Employees may already exist, but one must match your login in this company.
                Ask a Team Hub administrator to check your employee profile's email for an admin login, or phone number for a phone login.</p>
            <p v-if="$store.state.user.email">Signed in as {{ $store.state.user.email }}</p>
            <router-link v-if="$store.state.user.teamHubRole === 'Admin'" to="/employees">Open Employees</router-link>
            <p>After the profile is updated, refresh this page.</p>
        </div>
        <template v-else>
        <div v-if="lastMeetingCapture && !lastMeetingCapture.jobId && lastMeetingCapture.meetingID === activeMeetingID && !showRunMeeting" class="meeting-capture-saved" role="status">
            No recording was saved for this run.<template v-if="lastMeetingCapture.reason"> {{ lastMeetingCapture.reason }}</template>
            <router-link to="/ai-hub#captures">My AI Captures</router-link>
        </div>
        <div class="meeting-groupbar">
            <div
                class="meeting-group"
                :class="activeGroup == 'home' ? 'active' : ''"
                @click="selectGroup('home')"
            >Home</div>
            <div
                class="meeting-group"
                :class="activeGroup == 'my' ? 'active' : ''"
                @click="selectGroup('my')"
            ><svg class="meeting-group-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>My Meetings</div>
            <div
                v-for="group in meetingGroups"
                :key="group.name"
                class="meeting-group"
                :class="activeGroup == group.name ? 'active' : ''"
                :style="activeGroup == group.name ? { borderBottomColor: group.color } : {}"
                @click="selectGroup(group.name)"
                @contextmenu="onGroupContextMenu($event, group)"
            >
                <span class="meeting-group-dot" :style="{ background: group.color }"></span>{{ group.name }}
            </div>
            <div v-if="$store.state.user.teamHubRole == 'Admin'" class="meeting-menu group-add">
                <span class="meeting-addlink" ref="addGroupTrigger" @click.stop="toggleAddGroup()">+ Add Group</span>
            </div>
        </div>

        <div v-if="activeGroup != 'home'" class="meeting-tabbar">
            <div
                v-for="meeting in meetings"
                :key="meeting.ID"
                class="meeting-tab"
                :class="activeMeetingID == meeting.ID ? 'active' : ''"
                @click="selectMeeting(meeting.ID)"
            >
                {{ meeting.Name }}<span class="meeting-tab-badge">{{ recurrenceLabel(meeting.RecurrenceType) }}</span>
            </div>
            <div class="meeting-menu new-meeting-add">
                <span class="meeting-addlink" ref="templatePickerTrigger" @click.stop="toggleTemplatePicker()">+ New Meeting</span>
            </div>
            <div class="meeting-menu past-meetings">
                <span class="meeting-addlink" ref="pastMeetingsTrigger" @click.stop="togglePastMeetings()">Past Meetings</span>
            </div>
        </div>

        <!-- Positioned relative to .meeting-page (see positionMenuPopup) rather than nested inside
             the scrolling bars above, since those clip anything absolute that escapes their bounds -->
        <div v-if="showAddGroup" class="meeting-menu-pop add-group-pop" :style="addGroupPopupStyle">
            <div class="meeting-menu-head">Add a meeting group</div>
            <div v-if="!availableTeams.length" class="meeting-menu-empty">Every team already has a meeting group.</div>
            <template v-else>
                <select v-model="newGroupName">
                    <option value="" disabled>Choose a team</option>
                    <option v-for="team in availableTeams" :key="team" :value="team">{{ team }}</option>
                </select>
                <div class="meeting-menu-color">
                    <input type="color" v-model="newGroupColor" />
                    <span>Group color</span>
                </div>
                <div
                    class="meeting-btn meeting-btn-light"
                    :class="!newGroupName || savingGroup ? 'disable-button' : ''"
                    @click="addGroup()"
                >Add</div>
            </template>
        </div>
        <div v-if="showGroupMenu && groupMenuTarget" class="meeting-menu-pop group-edit-pop" :style="groupMenuPopupStyle">
            <div class="meeting-menu-head">{{ groupMenuTarget.name }}</div>
            <div class="meeting-menu-color">
                <input type="color" v-model="editGroupColor" />
                <span>Group color</span>
            </div>
            <div
                class="meeting-btn meeting-btn-light group-edit-btn"
                :class="savingGroupChange ? 'disable-button' : ''"
                @click="saveGroupColor()"
            >Save Color</div>
            <div class="meeting-btn meeting-btn-light meeting-btn-danger group-edit-btn" @click="confirmDeleteGroup()">Delete Group</div>
        </div>
        <div v-if="showPastMeetings" class="meeting-menu-pop past-meetings-pop" :style="pastMeetingsPopupStyle">
            <div class="meeting-menu-head">Past Meetings</div>
            <div v-if="!pastMeetingsLoaded" class="meeting-menu-empty">Loading&hellip;</div>
            <div v-else-if="!pastMeetings.length" class="meeting-menu-empty">No past meetings yet.</div>
            <div v-else class="meeting-menu-list">
                <div v-for="m in pastMeetings" :key="m.ID" class="meeting-menu-item" @click="selectPastMeeting(m)">
                    <span class="meeting-menu-item-name">{{ m.Name }}</span>
                    <span class="meeting-menu-item-date">{{ pastMeetingDateDisplay(m.Date) }}</span>
                </div>
            </div>
        </div>
        <div v-if="showTemplatePicker" class="meeting-menu-pop template-picker-pop" :style="templatePickerPopupStyle">
            <div class="meeting-menu-head">Create a meeting{{ activeGroup == 'my' ? '' : ` in ${activeGroup}` }}</div>
            <div class="meeting-menu-list">
                <div v-for="t in meetingTemplates" :key="t.ID" class="meeting-template-item" @click="selectTemplate(t)">
                    <span class="meeting-template-dot" :style="{ background: t.Color }"></span>
                    <div class="meeting-template-text">
                        <div class="meeting-template-name">{{ t.Name }}</div>
                        <div v-if="t.Description" class="meeting-template-desc">{{ t.Description }}</div>
                    </div>
                </div>
                <div class="meeting-template-item" @click="selectTemplate(null)">
                    <span class="meeting-template-dot meeting-template-dot-custom"></span>
                    <div class="meeting-template-text">
                        <div class="meeting-template-name">Custom</div>
                        <div class="meeting-template-desc">Name it and add sections &mdash; each with its own timer.</div>
                    </div>
                </div>
            </div>
            <div v-if="$store.state.user.teamHubRole == 'Admin'" class="meeting-template-manage" @click="manageTemplates()">Manage Templates</div>
        </div>

        <!-- Home: the week ahead, each group, and what's on your plate (MeetingsHome.vue) -->
        <meetings-home v-if="activeGroup == 'home'" :key="homeKey" @open-meeting="openFromHome($event)" @open-group="selectGroup($event)" @open-item="openItemFromHome($event)" />
        <div v-else-if="!meetings.length" class="meeting-empty-state">
            No meetings yet in {{ activeGroup == 'my' ? 'My Meetings' : activeGroup }}. Use <b>+ New Meeting</b> to add one.
        </div>

        <template v-else-if="activeMeetingID && meetingLoaded">

            <div :class="showRunMeeting ? 'run-meeting-overlay' : ''">
                <template v-if="showRunMeeting">
                    <div class="run-meeting-bar">
                        <div class="run-meeting-bar-left">
                            <span class="run-meeting-title">{{ activeMeeting.Name }}</span>
                            <div class="run-meeting-stat">
                                <div class="run-meeting-stat-time" :class="isSectionOverTime ? 'over' : ''">{{ currentSectionTimeDisplay }}</div>
                                <div class="run-meeting-stat-label">{{ (currentRunSection.name || '').toUpperCase() }}</div>
                            </div>
                            <div class="run-meeting-stat">
                                <div class="run-meeting-stat-time">{{ timeLeftDisplay }}</div>
                                <div class="run-meeting-stat-label">Time Left</div>
                            </div>
                            <div class="run-meeting-desc">
                                <div class="run-meeting-desc-name">{{ currentRunSection.name }}</div>
                                <div class="run-meeting-desc-text">{{ currentRunSection.description }}</div>
                            </div>
                        </div>
                        <!-- Hidden with v-show, never v-if: unmounting it would cut off a recording that's in progress -->
                        <ai-meeting-recorder v-if="showsRecorder" v-show="!(meetingStarted && hideRecorder)" class="run-meeting-recorder" ref="meetingRecorder" @ready="jobId => lastMeetingCapture = { jobId, meetingID: activeMeetingID }" controlled :key="activeMeetingID" :meeting-id="activeMeetingID" :history-id="currentHistory?.ID" :run-skipped="!!currentHistory?.RecordingSkipped" />
                        <div class="run-meeting-bar-right">
                            <!-- Opens the attendee picker; its Start Meeting button starts the meeting (onSaveRunAttendees) -->
                            <div v-if="!meetingStarted" class="run-meeting-btn run-meeting-btn-attend" :class="startingMeeting ? 'disable-button' : ''" @click="showSelectAttendees = true">Start Meeting</div>
                            <div v-if="meetingStarted && showsRecorder" class="run-meeting-btn" @click="hideRecorder = !hideRecorder">{{ hideRecorder ? 'Show Recording' : 'Hide Recording' }}</div>
                            <div class="run-meeting-btn" @click="nextSection()">Next</div>
                            <div class="run-meeting-btn">Edit</div>
                            <!-- Back before the meeting starts (nothing running or recording yet); End meeting once it's running -->
                            <div v-if="!meetingStarted" class="run-meeting-btn run-meeting-btn-end" @click="closeRunMeeting()">Back</div>
                            <div v-else-if="endingMeeting" class="run-meeting-btn run-meeting-btn-end disable-button">Ending…</div>
                            <!-- Opens the End of Meeting pop-up (rating + new to-dos), whose End Meeting ends it -->
                            <div v-else class="run-meeting-btn run-meeting-btn-end" @click="showEndPanel = true">End meeting</div>
                        </div>
                    </div>
                    <!-- run-meeting-sections-fill: the cards share the full width equally (remove it for content-width cards) -->
                    <div class="run-meeting-sections run-meeting-sections-fill">
                        <div
                            v-for="(section, i) in meetingSections"
                            :key="i"
                            class="run-meeting-section-card"
                            :class="i == activeSectionIndex ? 'active' : ''"
                            @click="selectRunSection(i)"
                        >
                            <div class="run-meeting-section-top">
                                <span class="run-meeting-section-num">{{ i + 1 }}</span>
                                <span class="run-meeting-section-min">{{ section.minutes }}m</span>
                            </div>
                            <div class="run-meeting-section-name">{{ section.name }}</div>
                        </div>
                    </div>
                </template>

                <div v-else class="meeting-head">
                    <div v-if="viewingPastMeeting" class="meeting-past-banner">
                        Viewing a past meeting
                        <span class="meeting-past-banner-back" @click="exitPastMeeting()">&larr; Back to current meetings</span>
                    </div>
                    <div class="meeting-head-top">
                        <div v-if="bigDate.md" class="meeting-bigdate">
                            <div class="meeting-bigdate-dow">{{ bigDate.dow }}</div>
                            <div class="meeting-bigdate-md">{{ bigDate.md }}</div>
                        </div>
                        <div class="meeting-head-title">
                            <ai-meeting-status v-if="activeMeetingID && !showRunMeeting && ['Admin', 'Editor'].includes($store.state.user.role)" :meeting-id="activeMeetingID" class="meeting-title-recording-status" />
                            <div class="meeting-head-name">
                                <input class="meeting-name-input" type="text" v-model="formData.Name" @change="saveMeetingData(true)" />
                                <span v-if="!activeMeeting.Team" class="meeting-private-pill">Private</span>
                                <span v-else class="meeting-team-pill" :style="teamPillStyle">{{ activeMeeting.Team }}</span>
                                <span v-if="headerAvatars.length" class="meeting-avatars">
                                    <span v-for="av in headerAvatars.slice(0, 3)" :key="av.id" class="meeting-avatar" :title="av.name">{{ av.initials }}</span>
                                    <span
                                        v-if="headerAvatars.length > 3"
                                        class="meeting-avatar meeting-avatar-more"
                                        :title="headerAvatars.slice(3).map(av => av.name).join(', ')"
                                    >+{{ headerAvatars.length - 3 }}</span>
                                </span>
                            </div>
                            <div class="meeting-head-meta">
                                <span class="meeting-recur">{{ recurrenceLabel(activeMeeting.RecurrenceType) }}</span>
                                <template v-if="timeDisplay">
                                    &middot;
                                    <span :class="activeMeeting.StartTime ? 'meeting-time' : 'meeting-time meeting-time-none'">{{ timeDisplay }}</span>
                                </template>
                                <template v-if="upcomingDates.length">
                                    &middot;
                                    <span class="meeting-uplbl">Upcoming</span>
                                    <span v-for="(d, i) in upcomingDates" :key="i" class="meeting-upchip">{{ d }}</span>
                                </template>
                            </div>
                        </div>
                        <div class="meeting-length-box">
                            <span class="meeting-total-length">{{ totalLengthDisplay }}</span>
                            <div class="meeting-btn meeting-btn-run" @click="openRunMeeting()">{{ currentHistory ? 'Join Meeting' : 'Run Meeting' }}</div>
                        </div>
                    </div>
                    <div class="meeting-head-actions">
                        <div class="meeting-subtabs">
                            <button type="button" :class="activeTab == 'board' ? 'on' : ''" @click="() => { activeTab = 'board'; viewingRecap = null; }">Board</button>
                            <button type="button" :class="activeTab == 'history' ? 'on' : ''" @click="() => { activeTab = 'history'; viewingRecap = null; }">History</button>
                        </div>
                        <div class="meeting-head-btns">
                            <!-- The Forge for this meeting, named for what it does (Keelan, 2 Nov 2026): Prep for my meeting (Issues) / Improve my meeting (check-up) (ForgeTeam) -->
                            <div v-if="!viewingPastMeeting" class="meeting-btn meeting-btn-forge" @click="showForge = true"><svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd"><path d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg>{{ forgeKinds.includes('prepl10') ? 'Prep for my meeting' : 'Improve my meeting' }}</div>
                            <div class="meeting-btn meeting-btn-light" @click="openEditMeeting()">Edit Meeting</div>
                            <div class="meeting-btn meeting-btn-light" @click="openShareAgenda()">Share Agenda</div>
                            <div class="meeting-btn meeting-btn-light" @click="editColumns = !editColumns">
                                {{ editColumns ? 'Hide Columns' : 'Edit Columns' }}
                            </div>
                            <div v-if="canDeleteActiveMeeting" class="meeting-btn meeting-btn-light meeting-btn-danger" @click="showConfirmDeleteMeeting = true">
                                Delete Meeting
                            </div>
                        </div>
                    </div>
                </div>

                <div class="meeting-wrap">
                    <template v-if="activeTab == 'history'">
                        <template v-if="viewingRecap">
                            <div class="meeting-recap-back" @click="viewingRecap = null">&larr; Back to history</div>
                            <div class="meeting-recap-card">
                                <ai-meeting-recap v-if="['Admin', 'Editor'].includes($store.state.user.role)" :history-id="viewingRecap.ID" />
                                <div class="meeting-recap-title">{{ activeMeeting.Name }}</div>
                                <div class="meeting-recap-meta">{{ historyDateDisplay(viewingRecap.StartDate) }} &middot; {{ historyAttendeeCount(viewingRecap) }} attendees<template v-if="viewingRecap.Rating"> &middot; rated {{ viewingRecap.Rating }}/10</template></div>

                                <div class="meeting-recap-label">Attendees</div>
                                <div class="meeting-recap-attendees">{{ recapAttendeeNames(viewingRecap) }}</div>

                                <template v-for="(col, i) in recapBoard(viewingRecap)" :key="i">
                                    <div v-if="col.items && col.items.length" class="meeting-recap-col">
                                        <div class="meeting-recap-col-header">
                                            <span class="meeting-recap-col-dot" :style="{ background: col.color }"></span>{{ col.name }}
                                        </div>
                                        <div class="meeting-recap-item" v-for="(item, j) in col.items" :key="j">
                                            <div class="meeting-recap-item-row">
                                                <span class="meeting-recap-item-name">{{ item.Name }}<span v-if="item.Private" class="meeting-private" title="Only visible to you"><svg class="meeting-private-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>Only you</span></span>
                                                <span v-if="employeeName(item.AssigneeEmployeeID)" class="meeting-recap-item-assignee">{{ employeeName(item.AssigneeEmployeeID) }}</span>
                                            </div>
                                            <div v-if="item.Comments && item.Comments.length" class="meeting-recap-item-comments">
                                                <div class="meeting-recap-item-comment" v-for="(c, k) in item.Comments" :key="k">
                                                    <span class="meeting-recap-item-comment-author">{{ c.AuthorName || 'Unknown' }}:</span> {{ c.Comment }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <div v-if="!historyActionItemCount(viewingRecap)" class="meeting-recap-empty">No action items were recorded for this meeting.</div>
                            </div>
                        </template>

                        <template v-else>
                            <div v-if="!meetingHistory.length" class="meeting-empty-state">No meeting history yet.</div>
                            <div v-else class="meeting-history-row" v-for="(h, i) in meetingHistory" :key="i" @click="viewingRecap = h">
                                <div class="meeting-history-date">{{ historyDateDisplay(h.StartDate) }}</div>
                                <div class="meeting-history-main">
                                    <div class="meeting-history-title">{{ activeMeeting.Name }}</div>
                                </div>
                                <div class="meeting-history-meta">
                                    <div>{{ historyAttendeeCount(h) }} attendees</div>
                                    <div>{{ historyActionItemCount(h) }} action items</div>
                                    <div v-if="h.Rating">Rated {{ h.Rating }}/10</div>
                                </div>
                                <div class="meeting-history-links"><ai-meeting-recap v-if="['Admin', 'Editor'].includes($store.state.user.role)" :history-id="h.ID" links-only /><span class="meeting-history-view">View recap &rarr;</span></div>
                            </div>
                        </template>
                    </template>

                    <template v-else>
                        <!-- The current section's team scorecard / rocks, when the section asks for them (AddEditMeeting) -->
                        <div v-if="runShowsScorecard" class="run-meeting-panel">
                            <div class="run-meeting-panel-h">{{ activeMeeting.Team }} Scorecard</div>
                            <scorecard-view :key="'sc-' + activeMeeting.Team" :team="activeMeeting.Team" :meeting="issueMeeting" />
                        </div>
                        <div v-if="runShowsRocks" class="run-meeting-panel">
                            <div class="run-meeting-panel-h">{{ activeMeeting.Team }} Rocks</div>
                            <rocks-view :key="'rk-' + activeMeeting.Team" :team="activeMeeting.Team" :meeting="issueMeeting" />
                        </div>

                        <!-- The same Edit Columns as on decks (EditColumnsPanel.vue) -->
                        <edit-columns-panel v-if="editColumns" :columns="formData.Columns" @save="saveMeetingData(false)" @cancel="cancelEditColumns()" @remove="removeCol" @add="(col) => formData.Columns.push(col)" />

                        <!-- How much of the board is checked off: completed items / all items -->
                        <div v-if="!runHidesBoard && boardProgress.total" class="meeting-progress">
                            <span class="meeting-progress-label">{{ boardProgress.done }} of {{ boardProgress.total }} done</span>
                            <div class="meeting-progress-track" role="progressbar" :aria-valuenow="boardProgress.percent" aria-valuemin="0" aria-valuemax="100" aria-label="Items done">
                                <div :style="{ width: boardProgress.percent + '%' }"></div>
                            </div>
                            <span class="meeting-progress-pct">{{ boardProgress.percent }}%</span>
                        </div>

                        <!-- Hidden while the section is set to Hide the board -->
                        <div v-if="!runHidesBoard" class="kanban">
                            <template v-for="(col, i) in formData.Columns">
                                <div v-if="col.show" :key="i" class="kanban-col kanban-col-tinted" :style="{ '--col-color': col.color }">
                                    <div class="kanban-colh">
                                        <span class="kanban-coldot" :style="{ background: col.color }"></span>
                                        <span style="flex:1">{{ col.name }}</span>
                                        <span class="kanban-coladd" title="Add item" @click="addEditItem(col.name, null)"><svg-icon icon="plus" /></span>
                                        <span class="kanban-colcnt">{{ (itemsByColumn[col.name] || []).length }}</span>
                                        <svg-icon @click="col.show = false" icon="arrowsin" class="kanban-colcollapse" />
                                    </div>
                                    <div class="kanban-colb">
                                        <!-- :sort="false": cards move between columns, but their order within one is set by sortColumn, not by hand -->
                                        <draggable class="kanban-cards" :list="itemsByColumn[col.name]" group="column" :sort="false" @change="dragEnd($event, col.name)" :animation="150" ghost-class="kanban-ghost" :empty-insert-threshold="40" :force-auto-scroll-fallback="true" :scroll-sensitivity="120" :scroll-speed="20" :delay="200" :delay-on-touch-only="true" :touch-start-threshold="5">
                                            <div v-for="item in itemsByColumn[col.name]" :key="item.ID" class="meeting-card" :class="item.Completed ? 'meeting-card-done' : ''" @click="addEditItem(null, item)">
                                                <div class="meeting-card-title"><input type="checkbox" :checked="item.Completed" @click.stop :title="item.Completed ? 'Completed' : 'Mark completed'" @change="toggleItemComplete(item, $event)" />{{ item.Name }}</div>
                                                <div class="meeting-card-footer">
                                                    <span v-if="item.DueDate" class="meeting-due" :class="isItemOverdue(item) ? 'overdue' : ''"><svg-icon icon="clock" />{{ dueDateLabel(item.DueDate) }}</span>
                                                    <span v-if="item.Priority" class="meeting-pri" :class="item.Priority">{{ priorityLabel(item.Priority) }}</span>
                                                    <span v-if="item.Private" class="meeting-private" title="Only visible to you"><svg class="meeting-private-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>Only you</span>
                                                    <span v-if="item.TaskManagerOwners && item.TaskManagerOwners.length" class="meeting-on-tasks" :title="'On task manager: ' + item.TaskManagerOwners.join(', ')"><svg-icon icon="checklist" />{{ taskManagerFirstNames(item.TaskManagerOwners) }}</span>
                                                    <!-- Mirrored (sql/meetingitem-mirrors.sql): where it's from, or how many other meetings it's on -->
                                                    <span v-if="item.HomeMeeting" class="meeting-on-tasks" :title="'Mirrored from ' + item.HomeMeeting.Name"><svg-icon icon="copy" />{{ item.HomeMeeting.Name }}</span>
                                                    <span v-else-if="item.MirrorMeetings && item.MirrorMeetings.length" class="meeting-on-tasks" :title="'Also on ' + item.MirrorMeetings.map((m) => m.Name).join(', ')"><svg-icon icon="copy" />{{ item.MirrorMeetings.length }}</span>
                                                    <span v-if="assigneeInitials(item.AssigneeEmployeeID)" class="meeting-owner">{{ assigneeInitials(item.AssigneeEmployeeID) }}</span>
                                                </div>
                                            </div>
                                        </draggable>
                                        <span class="kanban-addbtn" @click="addEditItem(col.name, null)">+ Add item</span>
                                    </div>
                                </div>
                                <div v-else class="kanban-col kanban-col-collapsed kanban-col-tinted" :key="'a'+i" :style="{ '--col-color': col.color }" @click="col.show = true">
                                    <svg-icon icon="arrowsout" class="kanban-colcollapse" />
                                    <span>{{ col.name }}</span>
                                </div>
                            </template>
                        </div>
                    </template>
                </div>
            </div>
        </template>

        <add-edit-meeting v-if="showMeetingForm" :group="activeGroup" :meeting="meetingFormMeeting" :template="meetingFormTemplate" @close="showMeetingForm = false" @created="meetingCreated" @updated="meetingUpdated" />
        <!-- End of Meeting (End meeting in the run bar): what was added this run, and the meeting's rating (one score,
             anyone sets it; End Meeting needs it, askEndMeeting). sql/meeting-rating.sql. Before the item modal so a
             to-do opened from here shows on top. -->
        <div v-if="showEndPanel && showRunMeeting && currentHistory" class="error-msg-wrap" @click.self="closeEndPanel()">
            <div class="run-end slide-up">
                <div class="run-end-head">
                    <div class="run-end-title">End of Meeting</div>
                    <div class="run-end-meta">{{ activeMeeting.Name }} · {{ runDateDisplay }}</div>
                    <span class="run-end-x" title="Close" @click="closeEndPanel()"><svg-icon icon="x" /></span>
                </div>
                <div class="run-end-body">
                    <div class="run-end-h">New To-Dos<span>{{ newTodos.length ? newTodos.length + ' added this meeting' : 'Nothing added this meeting' }}</span></div>
                    <div v-if="newTodos.length" class="run-end-todos">
                        <div v-for="item in newTodos" :key="item.ID" class="run-end-todo" @click="addEditItem(null, item)">
                            <span class="run-end-todo-name">{{ item.Name }}</span>
                            <b v-if="item.AssigneeEmployeeID">{{ firstName(employeeName(item.AssigneeEmployeeID)) }}</b>
                            <span v-if="item.DueDate" class="run-end-todo-due">{{ dueDayDisplay(item.DueDate) }}</span>
                        </div>
                    </div>

                    <div class="run-end-h">Rate the Meeting<span>The team agrees on one score · goal 8+</span></div>
                    <div class="run-end-rates" role="radiogroup" aria-label="Meeting rating">
                        <button v-for="n in 10" :key="n" type="button" role="radio" :aria-checked="currentHistory.Rating == n" class="run-end-rate" :class="currentHistory.Rating == n ? 'on' : ''" @click="rateMeeting(n)">{{ n }}</button>
                    </div>
                </div>
                <!-- While recording, End Meeting asks here first, in the buttons' place -->
                <div v-if="confirmEnd && !endingMeeting" class="run-end-foot">
                    <span class="run-end-ask">Stop the recording and end the meeting?</span>
                    <div class="run-end-btns">
                        <div class="btn btn-light run-end-btn" @click="confirmEnd = false">No</div>
                        <div class="btn run-end-btn" @click="endMeeting()">Yes</div>
                    </div>
                </div>
                <div v-else class="run-end-foot">
                    <span class="run-end-last">{{ lastRating ? 'Last meeting: ' + lastRating : '' }}</span>
                    <div class="run-end-btns">
                        <div class="btn btn-light run-end-btn" @click="closeEndPanel()">Cancel</div>
                        <div class="btn run-end-btn" :class="!currentHistory.Rating || endingMeeting ? 'disable-button' : ''" :title="currentHistory.Rating ? '' : 'Rate the meeting first'" @click="askEndMeeting()">{{ endingMeeting ? 'Ending…' : 'End Meeting' }}</div>
                    </div>
                </div>
            </div>
        </div>
        <add-edit-meeting-item v-if="showAddEditItem" ref="addEditItemModal" :item="addEditData" :employees="itemAssigneeOptions" :columns="columnNames" @move-column="moveItemToColumn(addEditData.ID, $event)" @close="closeAddEdit()" @open-elsewhere="openMirroredItem($event)" />
        <move-column-prompt v-if="completedMove" message="Marked completed." :columns="columnNames" :current="completedMove.current" :default-column="completedMove.column" @move="moveItemToColumn(completedMove.itemID, $event); completedMove = null" @cancel="completedMove = null" />
        <!-- Share Agenda: the agenda for the date on the header's date card, as a PDF by email and/or text (meetings.ts /:id/agenda) -->
        <div v-if="shareAgenda" class="error-msg-wrap">
            <div class="form-wrapper slide-up crm-form-wrapper crm-form-new">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="shareAgenda = null">&times;</button>
                </div>
                <div class="qc-item-wrap crm-item-wrap crm-item-wrap-bare">
                    <div>
                        <h3 class="quick-add-heading">Share Agenda</h3>
                        <p class="meeting-share-hint">{{ activeMeeting.Name }}{{ agendaLabels.DateLabel ? ' · ' + agendaLabels.DateLabel : '' }}. Sent as a PDF: attached to the email, and as a link by text. The link works for 30 days.</p>
                        <div class="crm-item-label">Email</div>
                        <input type="text" style="margin:0" v-model="shareAgenda.email" placeholder="Email" />
                        <div class="crm-item-label">Phone</div>
                        <phone-input v-model="shareAgenda.phone" placeholder="Phone" />
                        <div class="meeting-share-actions">
                            <a class="meeting-share-preview" @click.prevent="previewAgenda()">Preview PDF</a>
                            <button type="button" class="btn" :class="shareAgenda.sending ? 'disable-button' : ''" @click="sendAgenda()">Send</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <pdf-pop-up v-if="agendaPDFUrl" :url="agendaPDFUrl" @close="agendaPDFUrl = ''" />

        <confirm-delete v-if="showConfirmDeleteMeeting" @confirm="deleteMeetingConfirm" @cancel="() => { showConfirmDeleteMeeting = false; }" msg="Are you sure you would like to delete this meeting? This cannot be undone."/>
        <confirm-delete
            v-if="showConfirmDeleteGroup"
            @confirm="deleteGroupConfirm"
            @cancel="() => { showConfirmDeleteGroup = false; groupMenuTarget = null; }"
            :msg="`Are you sure you would like to delete the '${groupMenuTarget && groupMenuTarget.name}' meeting group? Meetings already in it won't be deleted, but they'll no longer appear under a group tab.`"
        />
        <forge-team v-if="showForge && activeMeeting" modal :only="forgeKinds" :start-kind="forgeKinds[0]" :meeting="forgeMeeting" @applied="loadItems()" @close="showForge = false" />
        <select-meeting-attendees v-if="showSelectAttendees" :employees="teamEmployees" :attendee-ids="runAttendeeIDs" submit-label="Start Meeting" @close="showSelectAttendees = false" @save="onSaveRunAttendees" />
        </template>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import { VueDraggableNext } from 'vue-draggable-next';
import SvgIcon from '@/components/SvgIcon.vue';
import AddEditMeeting from '@/components/AddEditMeeting.vue';
import MeetingsHome from '@/components/MeetingsHome.vue';
import MoveColumnPrompt from '@/components/MoveColumnPrompt.vue';
import AddEditMeetingItem from '@/components/AddEditMeetingItem.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import SelectMeetingAttendees from '@/components/SelectMeetingAttendees.vue';
import ForgeTeam from '@/components/ForgeTeam.vue';
import AIMeetingRecorder from '@/components/AIMeetingRecorder.vue';
import AIMeetingRecap from '@/components/AIMeetingRecap.vue';
import AIMeetingStatus from '@/components/AIMeetingStatus.vue';
import ScorecardView from '@/components/ScorecardView.vue';
import RocksView from '@/components/RocksView.vue';
import PhoneInput from '@/components/PhoneInput.vue';
import EditColumnsPanel from '@/components/EditColumnsPanel.vue';
import PdfPopUp from '@/components/PDFPopUp.vue';
import { normalizePhone, PHONE_ERROR } from '@/phone';
import { nextOccurrence, occurrencesAfter, occurrenceLocal, occurrenceSortKey, occurrenceTimeLabel, durationLabel } from '@/meetingTime';
import { whenIdle } from '@/activity';
import { parseSections } from '@/meetingSections';
import { primaryColor } from '@/primaryColor';

const RECURRENCE_LABELS: any = { once: 'One time', daily: 'Daily', weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly' };
const PRIORITY_LABELS: any = { high: 'High', med: 'Med', low: 'Low' };

// Parses a plain yyyy-mm-dd string into a local-midnight Date, avoiding the timezone
// shift that `new Date('yyyy-mm-dd')` (parsed as UTC) can cause when displayed locally
function parseDateOnly(dateStr: string): Date {
    const [ y, m, d ] = dateStr.split('-').map((n: string) => parseInt(n));
    return new Date(y, m - 1, d);
}

// Meeting tabs, soonest next occurrence first (meetingTime.ts). A one-time meeting that has already ended today
// (the API keeps it listed until its date passes) goes after everything still to come, so it isn't picked first.
function sortByNextDate(meetings: any[]): any[] {
    const now = Date.now();
    const key = (m: any) => {
        const o = nextOccurrence(m);
        const ended = !!o.end && o.end.getTime() <= now;
        return { ended, time: occurrenceSortKey(o) };
    };
    return meetings.sort((a: any, b: any) => {
        const ka = key(a), kb = key(b);
        return ka.ended != kb.ended ? (ka.ended ? 1 : -1) : ka.time - kb.time;
    });
}

// H:MM:SS once there's an hour on the clock, MM:SS otherwise - matches the reference prototype's meetFmt
function formatSeconds(totalSecondsInput: number): string {
    const totalSeconds = Math.max(0, Math.round(totalSecondsInput));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');
    return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}
function formatMinutes(totalMinutes: number): string {
    return formatSeconds(totalMinutes * 60);
}

export default defineComponent({
    name: 'MeetingsView',
    components: {
        ForgeTeam, AiMeetingStatus: AIMeetingStatus, AiMeetingRecorder: AIMeetingRecorder, AiMeetingRecap: AIMeetingRecap, SvgIcon, AddEditMeeting, AddEditMeetingItem, ConfirmDelete, SelectMeetingAttendees, MeetingsHome, MoveColumnPrompt, ScorecardView, RocksView, PhoneInput, PdfPopUp, EditColumnsPanel,
        draggable: VueDraggableNext
    },
    data() {
        return {
            meetingGroups: [] as any,
            activeGroup: 'home', // 'home' (MeetingsHome), 'my' (My Meetings) or a group's team name
            homeKey: 0, // bumped to reload Home (the idle refresh)
            showAddGroup: false,
            addGroupPopupStyle: {} as any,
            showPastMeetings: false,
            pastMeetingsPopupStyle: {} as any,
            pastMeetings: [] as any,
            pastMeetingsLoaded: false,
            viewingPastMeeting: null as any,
            newGroupName: '',
            newGroupColor: primaryColor(),
            savingGroup: false,
            showGroupMenu: false,
            groupMenuPopupStyle: {} as any,
            groupMenuTarget: null as any,
            editGroupColor: primaryColor(),
            savingGroupChange: false,
            showConfirmDeleteGroup: false,

            employees: [] as any,

            meetings: [] as any,
            activeMeetingID: null as any,
            meetingLoaded: false,
            activeTab: 'board',
            formData: {
                ID: null as any,
                Name: '',
                Columns: [] as any
            },
            itemsByColumn: {} as any,
            showMeetingForm: false,
            meetingFormMeeting: null as any,
            meetingTemplates: [] as any,
            showTemplatePicker: false,
            templatePickerPopupStyle: {} as any,
            meetingFormTemplate: null as any,
            showAddEditItem: false,
            addEditData: {} as any,
            editColumns: false,
            columnsBeforeEdit: null as any, // the columns as they were when Edit Columns opened, for Cancel
            showConfirmDeleteMeeting: false,
            shareAgenda: null as any, // { email, phone, sending }: the Share Agenda popup
            agendaPDFUrl: '',
            showForge: false, // The Forge pop-up for this meeting

            showRunMeeting: false,
            activeSectionIndex: 0,
            currentHistory: null as any,
            meetingHistory: [] as any,
            viewingRecap: null as any,
            runSerial: 0,
            lastMeetingCapture: null as any,
            meetingStarted: false,
            stopIdleRefresh: null as any, // stops the idle refresh (activity.ts whenIdle)
            completedMove: null as any, // { itemID, current, column } while the move prompt after checking a card off is open
            startingMeeting: false,
            runAttendeeIDs: [] as any,
            showSelectAttendees: false,
            hideRecorder: false, // Hide Recording in Run Meeting (the recording keeps going)
            showEndPanel: false, // The End of Meeting pop-up (End meeting in the run bar)
            confirmEnd: false, // End Meeting clicked while recording: Yes/No shown in the pop-up's buttons' place
            endingMeeting: false, // Yes clicked: saving the recording and ending the run
            teamMemberIDs: null as number[] | null, // who can open the active team meeting: its team + Team Hub admins (see teamEmployees)

            sectionElapsedSeconds: 0,
            sectionStartedAt: null as any,
            timerInterval: null as any,
            eventSource: null as any,
            autoAdvancePending: false,
            meetingEventSource: null as any
        }
    },
    computed: {
        // The company's teams without a meeting group yet
        availableTeams(): string[] {
            return (this.$store.state.employeeTeams || []).filter((team: string) => {
                return !this.meetingGroups.some((g: any) => g.name == team);
            });
        },
        // The board's column names, for the item modal's move prompt after Send to tasks
        columnNames(): string[] {
            return (this.formData.Columns || []).map((c: any) => c.name);
        },
        activeMeeting(): any {
            // A past meeting opened via the Past Meetings picker isn't part of `meetings`
            // (the current/recurring list) on purpose - it never gets pinned into the tab row
            return this.viewingPastMeeting || this.meetings.find((m: any) => m.ID == this.activeMeetingID);
        },
        // Worked out by the API (same check its delete route uses), so this can't drift from the session
        canDeleteActiveMeeting(): boolean {
            return !!this.activeMeeting && !!this.activeMeeting.CanDelete;
        },
        teamPillStyle(): any {
            const group = this.meetingGroups.find((g: any) => g.name == this.activeMeeting?.Team);
            return group ? { color: group.color, borderColor: group.color } : {};
        },
        // The next occurrence (meetingTime.ts): the date card, its time, and the upcoming chips after it
        nextOccurrence(): any {
            return this.activeMeeting?.Date ? nextOccurrence(this.activeMeeting) : null;
        },
        bigDate(): { dow: string, md: string } {
            if(!this.nextOccurrence) {
                return { dow: '', md: '' };
            }
            const d = occurrenceLocal(this.nextOccurrence);
            return { dow: this.$store.state.dayShortStrings[d.getDay()].toUpperCase(), md: `${this.$store.state.monthShortStrings[d.getMonth()+1]} ${d.getDate()}` };
        },
        // "10:00 AM (9:00 AM CT) · 1 hr", or "No time set" on meetings from before times existed
        timeDisplay(): string {
            if(!this.nextOccurrence) {
                return '';
            }
            const time = occurrenceTimeLabel(this.activeMeeting, this.nextOccurrence);
            return time ? `${time} · ${durationLabel(this.activeMeeting.DurationMinutes)}` : 'No time set';
        },
        upcomingDates(): string[] {
            if(!this.nextOccurrence) {
                return [];
            }
            // The three meetings after the one on the date card
            return occurrencesAfter(this.activeMeeting, this.nextOccurrence, 3).map(o => {
                const d = occurrenceLocal(o);
                return `${this.$store.state.monthShortStrings[d.getMonth()+1]} ${d.getDate()}`;
            });
        },
        // How the agenda PDF shows the meeting's date (the date card's), time and recurrence
        agendaLabels(): { DateLabel: string, TimeLabel: string, RecurrenceLabel: string } {
            const o = this.nextOccurrence;
            const d = o ? occurrenceLocal(o) : null;
            return {
                DateLabel: d ? d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }) : '',
                TimeLabel: o && this.activeMeeting?.StartTime ? this.timeDisplay : '',
                RecurrenceLabel: this.activeMeeting ? this.recurrenceLabel(this.activeMeeting.RecurrenceType) : ''
            };
        },
        meetingSections(): any[] {
            return parseSections(this.activeMeeting?.SectionsJSON);
        },
        // What the Forge's meeting actions work from (ForgeTeam's meeting prop)
        // Prep the meeting is for whoever runs it (its creator or a Team Hub Admin, as for Delete Meeting, or a
        // Forge user); the read-only check-up is for everyone in it
        forgeKinds(): string[] {
            const u = this.$store.state.user;
            const runs = this.canDeleteActiveMeeting || (u.isAdmin && [ 'Admin', 'Editor' ].includes(u.role));
            return runs ? [ 'prepl10', 'meetingcheckup' ] : [ 'meetingcheckup' ];
        },
        forgeMeeting(): any {
            const m = this.activeMeeting || {};
            return {
                id: this.activeMeetingID, name: m.Name, team: m.Team || null,
                columns: (this.formData.Columns || []).map((c: any) => c.name),
                items: Object.values(this.itemsByColumn || {}).flat(),
                history: this.meetingHistory || [],
                plannedMinutes: this.meetingSections.reduce((sum: number, sec: any) => sum + (parseInt(sec.minutes) || 0), 0) || null,
                employees: this.teamEmployees
            };
        },
        totalLengthDisplay(): string {
            const totalMinutes = this.meetingSections.reduce((sum: number, s: any) => sum + (parseInt(s.minutes) || 0), 0);
            return formatMinutes(totalMinutes);
        },
        currentRunSection(): any {
            return this.meetingSections[this.activeSectionIndex] || { name: '', minutes: 0, description: '' };
        },
        // The recording box (Admins/Editors), unless this run was started with Skip recording
        showsRecorder(): boolean {
            return [ 'Admin', 'Editor' ].includes(this.$store.state.user.role) && !this.currentHistory?.RecordingSkipped;
        },
        // Run Meeting on a section that shows the team's scorecard / rocks above the board (team meetings only)
        runShowsScorecard(): boolean {
            return this.showRunMeeting && !!this.activeMeeting?.Team && !!this.currentRunSection.showScorecard;
        },
        // The meeting an Issue from the scorecard / rocks goes on (QuickAddAgendaItem), with the board's columns as
        // they are now
        issueMeeting(): any {
            return { ...this.activeMeeting, ColumnsJSON: JSON.stringify(this.formData.Columns || []) };
        },
        runShowsRocks(): boolean {
            return this.showRunMeeting && !!this.activeMeeting?.Team && !!this.currentRunSection.showRocks;
        },
        // The board's progress bar: checked-off items out of every item on the board
        boardProgress(): { done: number, total: number, percent: number } {
            const items: any[] = Object.values(this.itemsByColumn || {}).flat();
            const done = items.filter((item: any) => item.Completed).length;
            return { done, total: items.length, percent: items.length ? Math.round(done / items.length * 100) : 0 };
        },
        // In a run that's started and not being ended: leaving the page asks first (warnUnload, beforeRouteLeave)
        inRunningMeeting(): boolean {
            return this.showRunMeeting && this.meetingStarted && !!this.currentHistory && !this.endingMeeting;
        },
        // "Wed, Oct 21": the day the run started
        runDateDisplay(): string {
            const d = this.currentHistory?.StartDate ? new Date(this.currentHistory.StartDate) : new Date();
            return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        },
        // Items put on this board since the run started (tblMeetingItem.CreateDate; older items have none)
        newTodos(): any[] {
            if(!this.currentHistory?.StartDate) {
                return [];
            }
            const started = new Date(this.currentHistory.StartDate).getTime();
            const items: any[] = Object.values(this.itemsByColumn || {}).flat();
            return items.filter((item: any) => item.CreateDate && new Date(item.CreateDate).getTime() >= started);
        },
        // The rating of the last ended run that has one
        lastRating(): number | null {
            const prev = (this.meetingHistory || []).find((h: any) => h.EndDate && h.Rating && h.ID != this.currentHistory?.ID);
            return prev ? prev.Rating : null;
        },
        // Run Meeting on a section set to Hide the board (meetingSections.ts)
        runHidesBoard(): boolean {
            return this.showRunMeeting && !!this.currentRunSection.hideBoard;
        },
        currentSectionTimeDisplay(): string {
            if(!this.meetingStarted) {
                return formatMinutes(this.currentRunSection.minutes || 0);
            }
            // Counts down for display even though the underlying sectionElapsedSeconds counts up
            const targetSeconds = (this.currentRunSection.minutes || 0) * 60;
            return formatSeconds(Math.max(0, targetSeconds - this.sectionElapsedSeconds));
        },
        isSectionOverTime(): boolean {
            return this.meetingStarted && this.sectionElapsedSeconds > (this.currentRunSection.minutes || 0) * 60;
        },
        timeLeftDisplay(): string {
            if(!this.meetingStarted) {
                return this.totalLengthDisplay;
            }
            const sectionTargetSeconds = (this.currentRunSection.minutes || 0) * 60;
            const remainingInSection = Math.max(0, sectionTargetSeconds - this.sectionElapsedSeconds);
            const followingMinutes = this.meetingSections
                .slice(this.activeSectionIndex + 1)
                .reduce((sum: number, s: any) => sum + (parseInt(s.minutes) || 0), 0);
            return formatSeconds(remainingInSection + followingMinutes * 60);
        },
        headerAvatars(): any[] {
            if(!this.activeMeeting || this.activeMeeting.Team) {
                return [];
            }
            const ids = [ this.activeMeeting.CreatedByEmployeeID, ...(JSON.parse(this.activeMeeting.AttendeeEmployeeIDsJSON || '[]')) ];
            const seen = new Set();
            const out: any[] = [];
            for(const id of ids) {
                if(id == null || seen.has(id)) {
                    continue;
                }
                seen.add(id);
                const emp = this.employees.find((e: any) => e.ID == id);
                if(emp) {
                    out.push({ id, name: emp.Name, initials: this.initials(emp.Name) });
                }
            }
            return out;
        },
        // A team meeting's pickers only offer who can open it (the team plus Team Hub admins: teamMemberIDs, /api/meetings/attendee-options),
        // plus its saved and current-run attendees so they can still be unchecked
        teamEmployees(): any[] {
            if(!this.activeMeeting?.Team || !this.teamMemberIDs) {
                return this.employees;
            }
            const ids = [ ...this.teamMemberIDs, ...(JSON.parse(this.activeMeeting.AttendeeEmployeeIDsJSON || '[]')), ...(this.runAttendeeIDs || []) ];
            return this.employees.filter((e: any) => ids.includes(e.ID));
        },
        // The open item's current assignee stays in the list either way, so the picker still shows them
        itemAssigneeOptions(): any[] {
            const assigneeID = this.addEditData?.AssigneeEmployeeID;
            if(!this.activeMeeting || this.activeMeeting.Team) {
                const options = this.teamEmployees;
                const assignee = assigneeID && !options.some((e: any) => e.ID == assigneeID) ? this.employees.find((e: any) => e.ID == assigneeID) : null;
                return assignee ? [ ...options, assignee ] : options;
            }
            const ids = [ this.activeMeeting.CreatedByEmployeeID, ...(JSON.parse(this.activeMeeting.AttendeeEmployeeIDsJSON || '[]')) ];
            return this.employees.filter((e: any) => ids.includes(e.ID));
        }
    },
    async created() {
        while(!this.$store.state.user.teamHubEmployeeLoaded) {
            await new Promise(resolve => setTimeout(resolve, 200));
        }

        if(!this.$store.state.user.teamHubEmployee) {
            this.$store.state.routeLoaded = true;
            return;
        }

        await this.getEmployees();
        await this.getGroups();
        // Restore the specific board when returning from notes, including past/team meetings.
        const returnID = Number(this.$route.query.meetingID);
        let returningMeeting: any = null;
        if (returnID) {
            try {
                returningMeeting = (await axios.get(`${this.$store.state.apiUrl}/api/meetings/${returnID}`)).data;
                this.activeGroup = returningMeeting.Team || 'my';
            } catch { /* Normal meeting list remains available if access changed. */ }
        }
        await this.getMeetings();
        if (returningMeeting) {
            if (!this.meetings.some((m: any) => m.ID === returnID)) this.viewingPastMeeting = returningMeeting;
            this.activeMeetingID = returnID;
            await this.loadMeeting();
            // ?itemID opens that card too (the "From <meeting>" link on a task manager card)
            const returnItemID = Number(this.$route.query.itemID);
            if (returnItemID) {
                const item = Object.values(this.itemsByColumn).flat().find((it: any) => it.ID == returnItemID);
                if (item) this.addEditItem(null, item);
            }
        }
        await this.getMeetingTemplates();

        this.$store.state.routeLoaded = true;
        document.addEventListener('click', this.closeMenusOnOutsideClick);
        window.addEventListener('resize', this.closeMenusOnResize);
        // Left open: after 10 minutes untouched (or coming back to the tab after that long), refresh the list so the
        // soonest meeting is selected again and new or renamed meetings show up
        this.stopIdleRefresh = whenIdle(10 * 60000, () => this.idleRefresh());
        // Closing the tab, reloading or typing another address mid-meeting: the browser's own "Leave site?" (it shows
        // its own wording; a page can't change it or stop the person leaving)
        window.addEventListener('beforeunload', this.warnUnload);
    },
    // Another page in the app mid-meeting: asked first (the run itself keeps going for everyone else)
    beforeRouteLeave(to: any, from: any, next: any) {
        if(this.inRunningMeeting && !window.confirm('This meeting is still running. Leave without ending it?')) {
            next(false);
            return;
        }
        next();
    },
    beforeUnmount() {
        window.removeEventListener('beforeunload', this.warnUnload);
        this.stopIdleRefresh?.();
        document.removeEventListener('click', this.closeMenusOnOutsideClick);
        window.removeEventListener('resize', this.closeMenusOnResize);
        this.stopSectionTimer();
        this.closeEventSource();
        this.closeMeetingEventSource();
    },
    watch: {
        // Cancel puts the columns back as they were when Edit Columns opened
        editColumns(open: boolean) {
            this.columnsBeforeEdit = open ? JSON.parse(JSON.stringify(this.formData.Columns || [])) : null;
        }
    },
    methods: {
        recurrenceLabel(type: string): string {
            return RECURRENCE_LABELS[type] || type;
        },
        priorityLabel(p: string): string {
            return PRIORITY_LABELS[p] || p;
        },
        // "Oct 8", or "Oct 8, 2027" outside this year (DueDate is "YYYY-MM-DD")
        dueDateLabel(date: string): string {
            const [ y, m, d ] = date.split('-').map((n: string) => parseInt(n));
            return `${this.$store.state.monthShortStrings[m]} ${d}${y != new Date().getFullYear() ? ', ' + y : ''}`;
        },
        // Past its due date (the day after) and not completed
        isItemOverdue(item: any): boolean {
            if(!item.DueDate || item.Completed) {
                return false;
            }
            const now = new Date();
            const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            return item.DueDate < today;
        },
        // The card's checkbox: completes the item (or reopens it) in place; the broadcast brings everyone's boards up to date
        async toggleItemComplete(item: any, event: any) {
            const completed = !!event.target.checked;
            item.Completed = completed;
            // Checked-off items sort to the bottom of the column
            this.sortColumn(item.Column);
            let saved = false;
            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems/complete`, { MeetingID: this.activeMeetingID, ID: item.ID, Completed: completed })
                .then(() => {
                    saved = true;
                })
                .catch(e => {
                    console.log(e);
                    item.Completed = !completed;
                    event.target.checked = !completed;
                    this.sortColumn(item.Column);
                    alert(e?.response?.data || 'There was an error');
                })
            // Checked off: offer to move it to the last column (MoveColumnPrompt), unless it's there already
            const last = this.columnNames[this.columnNames.length - 1];
            if(saved && completed && this.columnNames.length >= 2 && item.Column != last) {
                this.completedMove = { itemID: item.ID, current: item.Column, column: last };
            }
        },
        initials(name: string): string {
            return (name || '').split(' ').filter(Boolean).map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
        },
        assigneeInitials(id: any): string {
            if(!id) {
                return '';
            }
            const emp = this.employees.find((e: any) => e.ID == id);
            return emp ? this.initials(emp.Name) : '';
        },
        // "Justin" / "Justin, Sam" / "Justin +2": whose task managers a card is on (full names in its title)
        taskManagerFirstNames(names: string[]): string {
            const first = names.map((n: string) => String(n || '').trim().split(/\s+/)[0]);
            return first.length > 2 ? `${first[0]} +${first.length - 1}` : first.join(', ');
        },
        async getEmployees() {
            await axios.get(`${this.$store.state.apiUrl}/api/employees/limitedinfo`)
                .then(resp => {
                    // tblEmployee.ID comes back from pg as a string (bigint), while everything
                    // that references it here (AttendeeEmployeeIDsJSON, AssigneeEmployeeID,
                    // employeeID) is a real number - normalize so comparisons against it match.
                    this.employees = resp.data.map((emp: any) => ({ ...emp, ID: Number(emp.ID) }));
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async getGroups() {
            await axios.get(`${this.$store.state.apiUrl}/api/meetings/groups`)
                .then(resp => {
                    this.meetingGroups = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        selectGroup(name: string) {
            if(this.activeGroup == name) {
                return;
            }
            this.activeGroup = name;
            this.getMeetings();
        },
        async getMeetingTemplates() {
            await axios.get(`${this.$store.state.apiUrl}/api/meetingtemplates`)
                .then(resp => {
                    this.meetingTemplates = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        // If no templates are saved, "+ New Meeting" skips straight to the Custom form, exactly
        // like it did before templates existed - the picker only shows up once there's a choice.
        toggleTemplatePicker() {
            if(!this.meetingTemplates.length) {
                this.openAddMeeting();
                return;
            }
            this.showTemplatePicker = !this.showTemplatePicker;
            this.showAddGroup = false;
            this.showPastMeetings = false;
            if(this.showTemplatePicker) {
                this.positionMenuPopup('templatePickerTrigger', 'templatePickerPopupStyle');
            }
        },
        selectTemplate(template: any) {
            this.showTemplatePicker = false;
            this.meetingFormTemplate = template;
            this.meetingFormMeeting = null;
            this.showMeetingForm = true;
        },
        manageTemplates() {
            this.showTemplatePicker = false;
            this.$router.push('/meeting-templates');
        },
        toggleAddGroup() {
            this.showAddGroup = !this.showAddGroup;
            this.showPastMeetings = false;
            this.newGroupName = '';
            this.newGroupColor = primaryColor();
            if(this.showAddGroup) {
                this.positionMenuPopup('addGroupTrigger', 'addGroupPopupStyle');
            }
        },
        togglePastMeetings() {
            this.showPastMeetings = !this.showPastMeetings;
            this.showAddGroup = false;
            if(this.showPastMeetings) {
                this.positionMenuPopup('pastMeetingsTrigger', 'pastMeetingsPopupStyle');
                if(!this.pastMeetingsLoaded) {
                    this.getPastMeetings();
                }
            }
        },
        // These popups are teleported to <body> (their triggers sit inside horizontally-scrolling,
        // overflow:auto bars, which would otherwise clip them no matter how high their z-index is),
        // so position has to be computed in JS instead of anchored via a CSS-positioned parent.
        positionMenuPopup(
            refName: 'addGroupTrigger' | 'pastMeetingsTrigger' | 'templatePickerTrigger',
            styleProp: 'addGroupPopupStyle' | 'pastMeetingsPopupStyle' | 'templatePickerPopupStyle'
        ) {
            const trigger = this.$refs[refName] as HTMLElement;
            const page = this.$refs.pageRoot as HTMLElement;
            if(!trigger || !page) {
                return;
            }
            // Offsets are relative to .meeting-page (position:relative), not the viewport, so the
            // popup scrolls naturally with the rest of the page instead of drifting like a
            // fixed-position one would - no scroll listener needed either. maxHeight caps it to
            // whatever viewport space is actually left below the trigger, so a long list (e.g.
            // several templates) scrolls internally instead of overflowing the screen.
            const margin = 10;
            const triggerRect = trigger.getBoundingClientRect();
            const pageRect = page.getBoundingClientRect();
            const popupWidth = 260;
            const rawLeft = triggerRect.left - pageRect.left;
            const maxLeft = pageRect.width - 10 - popupWidth;
            const left = Math.max(10, Math.min(rawLeft, maxLeft));
            const top = triggerRect.bottom - pageRect.top + 6;
            this[styleProp] = {
                top: `${top}px`,
                left: `${left}px`,
                maxHeight: `${Math.max(120, window.innerHeight - triggerRect.bottom - margin)}px`
            };
        },
        closeMenusOnOutsideClick(e: MouseEvent) {
            const target = e.target as HTMLElement;
            if(this.showAddGroup && !target.closest('.group-add') && !target.closest('.add-group-pop')) {
                this.showAddGroup = false;
            }
            if(this.showPastMeetings && !target.closest('.past-meetings') && !target.closest('.past-meetings-pop')) {
                this.showPastMeetings = false;
            }
            if(this.showTemplatePicker && !target.closest('.new-meeting-add') && !target.closest('.template-picker-pop')) {
                this.showTemplatePicker = false;
            }
            if(this.showGroupMenu && !target.closest('.group-edit-pop')) {
                this.closeGroupMenu();
            }
        },
        closeMenusOnResize() {
            this.showAddGroup = false;
            this.showPastMeetings = false;
            this.showTemplatePicker = false;
            this.closeGroupMenu();
        },
        async getPastMeetings() {
            const params = this.activeGroup == 'my' ? 'past=1' : `team=${encodeURIComponent(this.activeGroup)}&past=1`;
            await axios.get(`${this.$store.state.apiUrl}/api/meetings?${params}`)
                .then(resp => {
                    this.pastMeetings = resp.data;
                    this.pastMeetingsLoaded = true;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        selectPastMeeting(m: any) {
            this.showPastMeetings = false;
            this.viewingPastMeeting = m;
            this.activeMeetingID = m.ID;
            this.loadMeeting();
        },
        exitPastMeeting() {
            this.viewingPastMeeting = null;
            if(this.meetings.length) {
                this.selectMeeting(this.meetings[0].ID);
            } else {
                this.clearBoard();
            }
        },
        pastMeetingDateDisplay(dateStr: string): string {
            const dateOnly = new Date(dateStr).toISOString().split('T')[0];
            const d = parseDateOnly(dateOnly);
            return `${this.$store.state.monthShortStrings[d.getMonth()+1]} ${d.getDate()}, ${d.getFullYear()}`;
        },
        async addGroup() {
            if(!this.newGroupName || this.savingGroup) {
                return;
            }
            this.savingGroup = true;

            const groups = [...this.meetingGroups, { name: this.newGroupName, color: this.newGroupColor }];

            await axios.post(`${this.$store.state.apiUrl}/api/meetings/groups`, { groups })
                .then(() => {
                    this.meetingGroups = groups;
                    this.selectGroup(this.newGroupName);
                    this.showAddGroup = false;
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error adding this meeting group');
                })

            this.savingGroup = false;
        },
        // Right-click on a team group tab (admins only) opens a menu to recolor or
        // delete it - same underlying save as Add Group, just editing/filtering the existing list
        onGroupContextMenu(e: MouseEvent, group: any) {
            if(this.$store.state.user.teamHubRole != 'Admin') {
                return;
            }
            e.preventDefault();
            this.showAddGroup = false;
            this.showPastMeetings = false;
            this.groupMenuTarget = group;
            this.editGroupColor = group.color;
            this.showGroupMenu = true;
            this.positionGroupMenuPopup(e);
        },
        // Positioned at the click point (relative to .meeting-page, same reasoning as
        // positionMenuPopup) rather than anchored to a trigger element, since this opens whichever
        // group tab was right-clicked instead of a single fixed link
        positionGroupMenuPopup(e: MouseEvent) {
            const page = this.$refs.pageRoot as HTMLElement;
            if(!page) {
                return;
            }
            const pageRect = page.getBoundingClientRect();
            const popupWidth = 220;
            const rawLeft = e.clientX - pageRect.left;
            const maxLeft = pageRect.width - 10 - popupWidth;
            const left = Math.max(10, Math.min(rawLeft, maxLeft));
            this.groupMenuPopupStyle = {
                top: `${e.clientY - pageRect.top + 6}px`,
                left: `${left}px`
            };
        },
        closeGroupMenu() {
            this.showGroupMenu = false;
            this.groupMenuTarget = null;
        },
        async saveGroupColor() {
            if(!this.groupMenuTarget || this.savingGroupChange) {
                return;
            }
            this.savingGroupChange = true;

            const groups = this.meetingGroups.map((g: any) => g.name == this.groupMenuTarget.name ? { ...g, color: this.editGroupColor } : g);

            await axios.post(`${this.$store.state.apiUrl}/api/meetings/groups`, { groups })
                .then(() => {
                    this.meetingGroups = groups;
                    this.closeGroupMenu();
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error updating this meeting group');
                })

            this.savingGroupChange = false;
        },
        confirmDeleteGroup() {
            this.showGroupMenu = false;
            this.showConfirmDeleteGroup = true;
        },
        async deleteGroupConfirm() {
            this.showConfirmDeleteGroup = false;
            const target = this.groupMenuTarget;
            if(!target) {
                return;
            }

            const groups = this.meetingGroups.filter((g: any) => g.name != target.name);

            await axios.post(`${this.$store.state.apiUrl}/api/meetings/groups`, { groups })
                .then(() => {
                    this.meetingGroups = groups;
                    // The deleted group's meetings are untouched, but its tab is gone - fall back
                    // to My Meetings if that's what was showing
                    if(this.activeGroup == target.name) {
                        this.selectGroup('my');
                    }
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error deleting this meeting group');
                })

            this.groupMenuTarget = null;
        },
        async getMeetings() {
            this.meetings = [];
            this.clearBoard();
            this.editColumns = false;
            this.pastMeetings = [];
            this.pastMeetingsLoaded = false;
            // Home loads its own data (MeetingsHome.vue)
            if(this.activeGroup == 'home') {
                return;
            }

            const params = this.activeGroup == 'my' ? '' : `?team=${encodeURIComponent(this.activeGroup)}`;
            await axios.get(`${this.$store.state.apiUrl}/api/meetings${params}`)
                .then(resp => {
                    this.meetings = sortByNextDate(resp.data);
                })
                .catch(e => {
                    console.log(e);
                })

            if(this.meetings.length) {
                await this.selectMeeting(this.meetings[0].ID);
            }
        },
        clearBoard() {
            this.activeMeetingID = null;
            this.meetingLoaded = false;
            this.viewingPastMeeting = null;
            this.formData = { ID: null, Name: '', Columns: [] };
            this.itemsByColumn = {};
        },
        // Home's Open: that meeting's group, with the meeting selected
        async openFromHome(meeting: any) {
            this.activeGroup = meeting.Team || 'my';
            await this.getMeetings();
            if(this.meetings.some((m: any) => m.ID == meeting.ID)) {
                await this.selectMeeting(meeting.ID);
            }
        },
        // Home's On your plate: the item's meeting, with the item open
        async openItemFromHome(item: any) {
            this.activeGroup = item.Team || 'my';
            await this.getMeetings();
            // A past one-time meeting isn't in the group's list, so it opens as a past meeting (as from a task manager link)
            if(!this.meetings.some((m: any) => m.ID == item.MeetingID)) {
                try {
                    this.viewingPastMeeting = (await axios.get(`${this.$store.state.apiUrl}/api/meetings/${item.MeetingID}`)).data;
                } catch(e) {
                    console.log(e);
                    return;
                }
            }
            this.activeMeetingID = item.MeetingID;
            await this.loadMeeting();
            const card = Object.values(this.itemsByColumn).flat().find((it: any) => it.ID == item.ID);
            if(card) {
                this.addEditItem(null, card);
            }
        },
        selectMeeting(id: number) {
            this.viewingPastMeeting = null;
            this.activeMeetingID = id;
            return this.loadMeeting();
        },
        async loadMeeting() {
            this.meetingLoaded = false;
            this.editColumns = false;
            this.activeTab = 'board';
            this.viewingRecap = null;
            this.showRunMeeting = false;
            this.meetingStarted = false;
            this.currentHistory = null;
            this.stopSectionTimer();
            this.closeEventSource();
            this.closeMeetingEventSource();

            await axios.get(`${this.$store.state.apiUrl}/api/meetings/${this.activeMeetingID}`)
                .then(resp => {
                    this.formData.ID = resp.data.ID;
                    this.formData.Name = resp.data.Name;
                    this.formData.Columns = JSON.parse(resp.data.ColumnsJSON || '[]');
                    for(const col of this.formData.Columns) {
                        col.show = true;
                    }
                })
                .catch(e => {
                    console.log(e);
                })

            this.teamMemberIDs = null;
            const team = this.activeMeeting?.Team;
            if(team) {
                await axios.get(`${this.$store.state.apiUrl}/api/meetings/attendee-options`, { params: { team } })
                    .then(resp => {
                        // Still the same meeting's team (another may have been picked meanwhile)
                        if(this.activeMeeting?.Team == team) {
                            this.teamMemberIDs = resp.data.map((id: any) => Number(id));
                        }
                    })
                    .catch(e => {
                        console.log(e);
                    })
            }

            await this.loadItems();
            await this.checkCurrentHistory();
            await this.loadHistory();
            this.openMeetingEventSource();

            this.meetingLoaded = true;
        },
        async checkCurrentHistory() {
            await axios.get(`${this.$store.state.apiUrl}/api/meetinghistory/current/${this.activeMeetingID}`)
                .then(resp => {
                    this.currentHistory = resp.data || null;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async loadHistory() {
            await axios.get(`${this.$store.state.apiUrl}/api/meetinghistory/${this.activeMeetingID}`)
                .then(resp => {
                    this.meetingHistory = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        historyDateDisplay(dateStr: string): string {
            // StartDate is a real timestamp (not a plain date), so this can read local date
            // parts directly - no need for the UTC-midnight-safe parsing the DATE columns need
            const d = new Date(dateStr);
            return `${this.$store.state.monthShortStrings[d.getMonth()+1]} ${d.getDate()}, ${d.getFullYear()}`;
        },
        historyAttendeeCount(history: any): number {
            return history.AttendeeEmployeeIDsJSON ? JSON.parse(history.AttendeeEmployeeIDsJSON).length : 0;
        },
        historyActionItemCount(history: any): number {
            return this.recapBoard(history).reduce((sum: number, col: any) => sum + (col.items ? col.items.length : 0), 0);
        },
        recapBoard(history: any): any[] {
            return history.BoardJSON ? JSON.parse(history.BoardJSON).reverse() : [];
        },
        employeeName(id: any): string {
            if(!id) {
                return '';
            }
            const emp = this.employees.find((e: any) => e.ID == id);
            return emp ? emp.Name : '';
        },
        recapAttendeeNames(history: any): string {
            const ids = history.AttendeeEmployeeIDsJSON ? JSON.parse(history.AttendeeEmployeeIDsJSON) : [];
            const names = ids.map((id: number) => this.employeeName(id)).filter(Boolean);
            return names.length ? names.join(', ') : 'None recorded';
        },
        // Shared by the initial load and applyMeetingBroadcast() below, so a live 'items' push
        // gets sorted into columns exactly the same way a fresh fetch does
        setItemsByColumn(items: any[]) {
            this.itemsByColumn = {};
            for(const col of this.formData.Columns) {
                this.itemsByColumn[col.name] = [];
            }
            for(const item of items) {
                if(this.itemsByColumn[item.Column]) {
                    this.itemsByColumn[item.Column].push(item);
                }
            }
            for(const column of Object.keys(this.itemsByColumn)) {
                this.sortColumn(column);
            }
        },
        // A column's order: open items before checked-off ones, then High, Medium, Low, no priority, then due date
        // (soonest first, none last), then oldest first. The board doesn't keep a hand-made order.
        sortColumn(column: string) {
            const rank: any = { high: 0, med: 1, low: 2 };
            (this.itemsByColumn[column] || []).sort((a: any, b: any) =>
                (a.Completed ? 1 : 0) - (b.Completed ? 1 : 0)
                || (rank[a.Priority] ?? 3) - (rank[b.Priority] ?? 3)
                || (a.DueDate || '9999-12-31').localeCompare(b.DueDate || '9999-12-31')
                || a.ID - b.ID);
        },
        async loadItems() {
            await axios.get(`${this.$store.state.apiUrl}/api/meetingitems/${this.activeMeetingID}`)
                .then(resp => {
                    this.setItemsByColumn(resp.data);
                })
                .catch(e => {
                    console.log(e);
                })
        },
        openAddMeeting() {
            this.meetingFormMeeting = null;
            this.meetingFormTemplate = null;
            this.showMeetingForm = true;
        },
        openEditMeeting() {
            this.meetingFormMeeting = this.activeMeeting;
            this.showMeetingForm = true;
        },
        meetingCreated(meeting: any) {
            this.showMeetingForm = false;
            this.meetingFormTemplate = null;
            this.meetings.push(meeting);
            sortByNextDate(this.meetings);
            this.selectMeeting(meeting.ID);
        },
        openRunMeeting() {
            this.runSerial++;
            this.activeTab = 'board';
            this.editColumns = false;
            this.hideRecorder = false;

            if(this.currentHistory) {
                this.meetingStarted = true;
                this.runAttendeeIDs = JSON.parse(this.currentHistory.AttendeeEmployeeIDsJSON || '[]');
                this.applyHistorySectionState(this.currentHistory);
                this.openEventSource();
                this.startSectionTimer();
            } else {
                this.meetingStarted = false;
                this.activeSectionIndex = 0;
                this.sectionStartedAt = null;
                this.sectionElapsedSeconds = 0;
                const attendeeIDs = JSON.parse(this.activeMeeting.AttendeeEmployeeIDsJSON || '[]');
                const selfID = this.$store.state.user.employeeID;
                if(selfID && !attendeeIDs.includes(selfID)) {
                    attendeeIDs.push(selfID);
                }
                this.runAttendeeIDs = attendeeIDs;
            }

            this.showRunMeeting = true;
        },
        closeRunMeeting() {
            this.showRunMeeting = false;
            this.showEndPanel = false;
            this.confirmEnd = false;
            this.stopSectionTimer();
            this.closeEventSource();
        },
        // Local state derived from a history row's ActiveSectionIndex/SectionStartedAt - called
        // whenever that row arrives, whether from our own request or another device's SSE push
        applyHistorySectionState(history: any) {
            this.activeSectionIndex = history.ActiveSectionIndex || 0;
            this.sectionStartedAt = history.SectionStartedAt;
            this.tickSectionTimer();
        },
        // Recomputes elapsed time in the current section from `now - sectionStartedAt` - never
        // stored, always derived, so there's nothing to keep in sync or drift between devices.
        // sectionStartedAt is a TIMESTAMPTZ from the server, so it round-trips as an unambiguous
        // instant regardless of either end's timezone (a plain "timestamp without time zone"
        // column doesn't - see meeting-history.sql).
        tickSectionTimer() {
            if(!this.sectionStartedAt) {
                this.sectionElapsedSeconds = 0;
                return;
            }
            this.sectionElapsedSeconds = Math.max(0, Math.floor((Date.now() - new Date(this.sectionStartedAt).getTime()) / 1000));
        },
        startSectionTimer() {
            if(this.timerInterval) {
                return;
            }
            this.timerInterval = setInterval(() => {
                this.tickSectionTimer();
                this.checkAutoAdvance();
            }, 1000);
        },
        stopSectionTimer() {
            if(this.timerInterval) {
                clearInterval(this.timerInterval);
                this.timerInterval = null;
            }
        },
        // Every open device notices independently when the current section runs out, but the
        // write is guarded server-side (ExpectedFromIndex) so whichever one gets there first wins
        // and the others just no-op against the row that's already moved on.
        checkAutoAdvance() {
            if(this.autoAdvancePending) {
                return;
            }
            const targetSeconds = (this.currentRunSection.minutes || 0) * 60;
            if(this.sectionElapsedSeconds >= targetSeconds && this.activeSectionIndex < this.meetingSections.length - 1) {
                this.autoAdvancePending = true;
                this.pushSectionChange(this.activeSectionIndex + 1, this.activeSectionIndex);
            }
        },
        // Observer/subscriber pattern (same as Classroom.vue): keep an SSE connection open to this
        // meeting history row so every device watching it gets the fresh section/timer state
        // pushed down - no polling.
        openEventSource() {
            this.closeEventSource();
            if(!this.currentHistory) {
                return;
            }
            const url = `${this.$store.state.apiUrl}/api/meetinghistory/${this.currentHistory.ID}/subscribe`;
            this.eventSource = new EventSource(url, { withCredentials: true });
            this.eventSource.onmessage = (event: MessageEvent) => {
                let raw;
                try {
                    raw = JSON.parse(event.data);
                } catch(e) {
                    return;
                }
                this.applyRemoteHistoryUpdate(raw);
            };
            this.eventSource.onerror = (e: any) => {
                // EventSource retries the connection on its own; nothing to do here.
                console.log('meeting history subscription error', e);
            };
        },
        closeEventSource() {
            if(this.eventSource) {
                this.eventSource.close();
                this.eventSource = null;
            }
        },
        async applyRemoteHistoryUpdate(raw: any) {
            const runSerial = this.runSerial;
            if(!this.currentHistory || raw.ID != this.currentHistory.ID) {
                return;
            }
            this.currentHistory = raw;
            if(raw.EndDate) {
                // Save the final capture chunk before a remote ending removes the recorder.
                if (this.$refs.meetingRecorder && !(await (this.$refs.meetingRecorder as any).finishBeforeEnd(false))) return;
                if (runSerial !== this.runSerial) return;
                this.currentHistory = null;
                this.meetingStarted = false;
                this.closeRunMeeting();
                this.loadHistory();
                return;
            }
            this.applyHistorySectionState(raw);
        },
        // Whole-board subscription (meetings.ts) - stays open for as long as this meeting's board
        // is on screen, independent of whether a run is active, so board edits and someone else
        // starting/ending a run both reach this device without polling or a refresh.
        openMeetingEventSource() {
            this.closeMeetingEventSource();
            const url = `${this.$store.state.apiUrl}/api/meetings/${this.activeMeetingID}/subscribe`;
            this.meetingEventSource = new EventSource(url, { withCredentials: true });
            this.meetingEventSource.onmessage = (event: MessageEvent) => {
                let raw;
                try {
                    raw = JSON.parse(event.data);
                } catch(e) {
                    return;
                }
                this.applyMeetingBroadcast(raw);
            };
            this.meetingEventSource.onerror = (e: any) => {
                console.log('meeting subscription error', e);
            };
        },
        closeMeetingEventSource() {
            if(this.meetingEventSource) {
                this.meetingEventSource.close();
                this.meetingEventSource = null;
            }
        },
        async applyMeetingBroadcast(raw: any) {
            const runSerial = this.runSerial;
            if(raw.type == 'items') {
                this.setItemsByColumn(raw.items);
            } else if(raw.type == 'comments') {
                // Only relevant if this device actually has that item's card open right now -
                // the modal owns its own comments state, so just hand the fresh list to it.
                const modal = this.$refs.addEditItemModal as any;
                if(modal && typeof modal.applyLiveComments == 'function') {
                    modal.applyLiveComments(raw.itemID, raw.comments);
                }
            } else if(raw.type == 'history') {
                // Drives the Run Meeting / Join Meeting button live - unlike
                // applyRemoteHistoryUpdate() above, this has to work even when currentHistory
                // isn't already set, since this is exactly how a device that's just looking at
                // the board (not in the run itself) learns a run has started at all
                if (!raw.history && this.showRunMeeting && this.meetingStarted) {
                    if (this.$refs.meetingRecorder && !(await (this.$refs.meetingRecorder as any).finishBeforeEnd(false))) return;
                if (runSerial !== this.runSerial) return;
                    this.meetingStarted = false;
                    this.closeRunMeeting();
                    this.loadHistory();
                }
                this.currentHistory = raw.history || null;
                if(this.showRunMeeting && this.meetingStarted && raw.history) {
                    this.applyHistorySectionState(raw.history);
                }
            }
        },
        warnUnload(e: any) {
            if(this.inRunningMeeting) {
                e.preventDefault();
                e.returnValue = '';
            }
        },
        firstName(name: string): string {
            return (name || '').trim().split(/\s+/)[0];
        },
        // A to-do's due date: the weekday when it's within the coming week, else "Oct 28"
        dueDayDisplay(date: string): string {
            const [ y, m, d ] = date.split('-').map(Number);
            const due = new Date(y, m - 1, d);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const days = Math.round((due.getTime() - today.getTime()) / 86400000);
            return days >= 0 && days < 7
                ? due.toLocaleDateString('en-US', { weekday: 'short' })
                : due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        },
        // One score for the run, for everyone in it (meetinghistory.ts POST /:id/rating; the SSE update brings it to
        // the other devices)
        async rateMeeting(n: number) {
            if(!this.currentHistory) {
                return;
            }
            const before = this.currentHistory.Rating;
            this.currentHistory.Rating = n;
            await axios.post(`${this.$store.state.apiUrl}/api/meetinghistory/${this.currentHistory.ID}/rating`, { Rating: n })
                .then(resp => {
                    if(resp.data) {
                        this.applyRemoteHistoryUpdate(resp.data);
                    }
                })
                .catch(e => {
                    console.log(e);
                    if(this.currentHistory) {
                        this.currentHistory.Rating = before;
                    }
                    alert(e?.response?.data || 'There was an error saving the rating');
                })
        },
        closeEndPanel() {
            if(this.endingMeeting) {
                return;
            }
            this.showEndPanel = false;
            this.confirmEnd = false;
        },
        askEndMeeting() {
            if(this.endingMeeting) {
                return;
            }
            // A run needs its rating first (End Meeting stays disabled until then)
            if(this.currentHistory && !this.currentHistory.Rating) {
                return;
            }
            if ((this.$refs.meetingRecorder as any)?.recording) this.confirmEnd = true;
            else this.endMeeting();
        },
        async endMeeting() {
            if (this.endingMeeting) return;
            this.confirmEnd = false;
            this.endingMeeting = true;
            try { await this.endRun(); } finally { this.endingMeeting = false; }
        },
        async endRun() {
            const runSerial = this.runSerial;
            const recorder = this.$refs.meetingRecorder as any;
            // Already confirmed (askEndMeeting): stop and save the recording without asking again
            if (recorder && !(await recorder.finishBeforeEnd(false))) return;
            // The reason (a refused start, an upload that never began) otherwise only ever showed beside the recorder
            if (recorder && !recorder.jobId && recorder.error) this.lastMeetingCapture = { meetingID: this.activeMeetingID, jobId: null, reason: String(recorder.error) };
            if(this.currentHistory) {
                await axios.post(`${this.$store.state.apiUrl}/api/meetinghistory/end`, { ID: this.currentHistory.ID })
                    .then(async () => {
                        if (runSerial !== this.runSerial) return;
                        this.currentHistory = null;
                        this.meetingStarted = false;
                        await this.loadHistory();
                    })
                    .catch(e => {
                        console.log(e);
                        alert('There was an error ending the meeting');
                    })
            }

            if (runSerial === this.runSerial) this.closeRunMeeting();
        },
        selectRunSection(i: number) {
            this.pushSectionChange(i);
        },
        nextSection() {
            if(this.activeSectionIndex < this.meetingSections.length - 1) {
                this.selectRunSection(this.activeSectionIndex + 1);
            }
        },
        async pushSectionChange(newIndex: number, fromIndex?: number) {
            if(!this.currentHistory) {
                return;
            }
            const data: any = { ActiveSectionIndex: newIndex };
            if(fromIndex !== undefined) {
                data.ExpectedFromIndex = fromIndex;
            }
            await axios.post(`${this.$store.state.apiUrl}/api/meetinghistory/${this.currentHistory.ID}/section`, data)
                .then(resp => {
                    if(resp.data) {
                        this.applyRemoteHistoryUpdate(resp.data);
                    }
                })
                .catch(e => {
                    console.log(e);
                })

            this.autoAdvancePending = false;
        },
        // The attendee picker's Start Meeting: keep the picks and start right away. Called straight from that click
        // (nothing awaited first), so the recorder's screen-share prompt still counts as coming from the click.
        onSaveRunAttendees(ids: number[]) {
            this.runAttendeeIDs = ids;
            if(ids.length < 2) {
                alert('Select at least one other attendee to start the meeting');
                return;
            }
            this.showSelectAttendees = false;
            this.startMeeting();
        },
        async startMeeting() {
            if(this.meetingStarted || this.startingMeeting) {
                return;
            }
            if(this.runAttendeeIDs.length < 2) {
                alert('Select at least one other attendee to start the meeting');
                this.showSelectAttendees = true;
                return;
            }
            this.startingMeeting = true;

            // recordingSkipped: started with Skip recording (AIMeetingRecorder), saved on the run for everyone who joins
            const createHistory = async (recordingSkipped = false) => {
                const resp = await axios.post(`${this.$store.state.apiUrl}/api/meetinghistory`, {
                    MeetingID: this.activeMeetingID, AttendeeEmployeeIDs: this.runAttendeeIDs, RecordingSkipped: recordingSkipped
                });
                this.currentHistory = resp.data;
                this.meetingStarted = true;
                this.applyHistorySectionState(resp.data);
                this.openEventSource();
                this.startSectionTimer();
                return { meetingID: this.activeMeetingID, historyID: resp.data.ID };
            };
            try {
                const recorder = this.$refs.meetingRecorder as any;
                if (recorder) await recorder.startForMeeting(createHistory);
                else await createHistory();
            } catch (e: any) { alert(e?.response?.data || 'There was an error starting the meeting'); }

            this.startingMeeting = false;
        },
        async meetingUpdated() {
            this.showMeetingForm = false;
            const id = this.activeMeetingID;

            // A past meeting won't be in the refreshed `meetings` list (it's never in there),
            // so re-fetch it directly instead of relying on list membership like the normal path
            if(this.viewingPastMeeting) {
                await axios.get(`${this.$store.state.apiUrl}/api/meetings/${id}`)
                    .then(resp => {
                        this.viewingPastMeeting = resp.data;
                    })
                    .catch(e => {
                        console.log(e);
                    })
                await this.loadMeeting();
                return;
            }

            await this.getMeetings();
            if(this.meetings.some((m: any) => m.ID == id)) {
                this.selectMeeting(id);
            }
        },
        cancelEditColumns() {
            if(this.columnsBeforeEdit) {
                this.formData.Columns = this.columnsBeforeEdit;
            }
            this.editColumns = false;
        },
        removeCol(i: number) {
            if(this.itemsByColumn[this.formData.Columns[i].name] && this.itemsByColumn[this.formData.Columns[i].name].length) {
                alert('This column has items and cannot be removed. Move the items to a new column before removing.')
                return;
            }
            this.formData.Columns.splice(i, 1);
        },
        async saveMeetingData(nameChange: boolean) {
            const data = {
                ID: this.formData.ID,
                Name: this.formData.Name,
                Columns: this.formData.Columns
            }
            await axios.post(`${this.$store.state.apiUrl}/api/meetings/update`, data)
                .then(() => {
                    if(this.activeMeeting) {
                        this.activeMeeting.Name = this.formData.Name;
                    }
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error.')
                })

            if(!nameChange) {
                this.editColumns = false;
                this.loadItems();
            }
        },
        addEditItem(col: string|null, editItem: any) {
            if(editItem) {
                this.addEditData = editItem;
            } else {
                this.addEditData = {
                    ID: null as any,
                    MeetingID: this.activeMeetingID,
                    Name: '',
                    Description: '',
                    Column: col,
                    Priority: null,
                    AssigneeEmployeeID: null
                }
            }
            this.showAddEditItem = true;
        },
        dragEnd(event: any, column: string) {
            if(event.added) {
                // Optimistic - saveItemColumn below also sets this server-side, but this keeps
                // the card's own Column field correct locally until the next broadcast/reload.
                event.added.element.Column = column;
                // Lands where the sort puts it, not where it was dropped
                this.sortColumn(column);
                this.saveItemColumn(event.added.element.ID, column);
            }
            this.$forceUpdate();
        },
        // Saves a card moved to another column (meetingitems.ts /move); where it sits in the column isn't stored
        async saveItemColumn(itemID: number, column: string) {
            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems/move`, {
                MeetingID: this.activeMeetingID,
                ID: itemID,
                Column: column
            })
                .catch(e => {
                    console.log(e);
                    alert('There was an error.')
                })
        },
        // The item modal's "move to another column" after Send to tasks: the same as dropping the card in that column
        // (dragEnd), saved the same way
        moveItemToColumn(itemID: number, column: string) {
            if(!this.itemsByColumn[column]) {
                return;
            }
            let item = null;
            for(const name of Object.keys(this.itemsByColumn)) {
                const i = this.itemsByColumn[name].findIndex((it: any) => it.ID == itemID);
                if(i >= 0) {
                    item = this.itemsByColumn[name].splice(i, 1)[0];
                    break;
                }
            }
            if(!item) {
                return;
            }
            item.Column = column;
            this.itemsByColumn[column].push(item);
            this.sortColumn(column);
            this.saveItemColumn(item.ID, column);
        },
        // The idle refresh (whenIdle above). Never while a meeting is running, something's open on top of the board,
        // a past meeting is being viewed, or someone is typing in a field. Re-sorts the group's meetings and only
        // switches to the first one when it isn't already selected, so an unchanged board doesn't flicker.
        async idleRefresh() {
            const busy = this.showRunMeeting || this.showAddEditItem || this.showMeetingForm || this.showConfirmDeleteMeeting
                || this.showTemplatePicker || this.showSelectAttendees || this.showAddGroup || this.showPastMeetings
                || this.showGroupMenu || this.showConfirmDeleteGroup || this.editColumns || this.viewingPastMeeting || this.completedMove
                || this.shareAgenda || this.agendaPDFUrl
                || [ 'INPUT', 'TEXTAREA', 'SELECT' ].includes(document.activeElement?.tagName || '')
                || (document.activeElement as HTMLElement)?.isContentEditable;
            if(busy || !this.$store.state.user.teamHubEmployee) {
                return;
            }
            // Home reloads itself: a new key remounts it
            if(this.activeGroup == 'home') {
                this.homeKey++;
                return;
            }
            const group = this.activeGroup;
            const params = group == 'my' ? '' : `?team=${encodeURIComponent(group)}`;
            let meetings: any[] | null = null;
            await axios.get(`${this.$store.state.apiUrl}/api/meetings${params}`)
                .then(resp => {
                    meetings = sortByNextDate(resp.data);
                })
                .catch(e => {
                    console.log(e);
                })
            // Skip if the request failed, or the person changed groups while it was loading
            if(!meetings || group != this.activeGroup) {
                return;
            }
            this.meetings = meetings;
            if(!this.meetings.length) {
                this.clearBoard();
            } else if(this.meetings[0].ID != this.activeMeetingID) {
                await this.selectMeeting(this.meetings[0].ID);
            }
        },
        closeAddEdit() {
            this.loadItems();
            this.showAddEditItem = false;
        },
        // The item modal's link to another meeting a mirrored item is on: that meeting, with the item open there
        async openMirroredItem(meeting: any) {
            this.showAddEditItem = false;
            await this.openItemFromHome({ MeetingID: meeting.ID, ID: meeting.ItemID, Team: meeting.Team });
        },
        openShareAgenda() {
            this.shareAgenda = { email: '', phone: '', sending: false };
        },
        async previewAgenda() {
            try {
                const resp = await axios.post(`${this.$store.state.apiUrl}/api/meetings/${this.activeMeeting.ID}/agenda`, { ...this.agendaLabels, Preview: true });
                this.agendaPDFUrl = this.$store.state.apiUrl + '/temppdf/' + resp.data.fileName;
            } catch(e: any) {
                console.log(e);
                alert(e?.response?.data || 'There was an error');
            }
        },
        async sendAgenda() {
            const share = this.shareAgenda;
            if(share.sending) {
                return;
            }
            const email = share.email.trim();
            if(!email && !share.phone) {
                alert('Enter an email and/or phone to share this with');
                return;
            }
            if(email && (!email.includes('@') || !email.includes('.'))) {
                alert('Make sure to use a valid email');
                return;
            }
            const phone = share.phone ? normalizePhone(share.phone) : null;
            if(share.phone && !phone) {
                alert(PHONE_ERROR);
                return;
            }
            share.sending = true;
            try {
                await axios.post(`${this.$store.state.apiUrl}/api/meetings/${this.activeMeeting.ID}/agenda`, { ...this.agendaLabels, Email: email || null, Phone: phone });
                this.shareAgenda = null;
                alert('Agenda has been shared!');
            } catch(e: any) {
                console.log(e);
                alert(e?.response?.data || 'There was an error');
                share.sending = false;
            }
        },
        async deleteMeetingConfirm() {
            await axios.delete(`${this.$store.state.apiUrl}/api/meetings/${this.activeMeetingID}`)
                .then(() => {
                    this.meetings = this.meetings.filter((m: any) => m.ID != this.activeMeetingID);
                    this.clearBoard();
                    if(this.meetings.length) {
                        this.selectMeeting(this.meetings[0].ID);
                    }
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error.')
                })

            this.showConfirmDeleteMeeting = false;
        }
    }
})
</script>

<style scoped>
.meeting-page {
    /* Positioning context for the group/past-meetings popups (see positionMenuPopup) */
    position: relative;
}

.meeting-capture-saved { padding: 16px 20px; background: #fff4e8; color: #59412b; font-size: 14px; }
.meeting-capture-saved a { margin-left: 12px; font-weight: 700; text-decoration: underline; }

/* Group row */
.meeting-groupbar { background: #fff; border-bottom: 1px solid var(--mid-gray); padding: 0 20px; display: flex; align-items: center; gap: 2px; overflow-x: auto; }
.meeting-group { display: flex; align-items: center; gap: 8px; padding: 12px 15px; font-weight: 700; font-size: 14px; color: var(--font-dark-gray); cursor: pointer; border-bottom: 3px solid transparent; white-space: nowrap; }
.meeting-group:hover { color: var(--dark-gray); }
.meeting-group.active { color: var(--dark-gray); border-bottom-color: var(--primary-color); }
.meeting-group-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
/* My Meetings is private to you: a lock where the groups have their color dot */
.meeting-group-lock { width: 13px; height: 13px; flex-shrink: 0; color: var(--primary-color); }

.meeting-addlink { color: var(--font-mid-gray); font-size: 13px; cursor: pointer; font-weight: 700; padding: 7px 10px; white-space: nowrap; }
.meeting-addlink:hover { color: var(--primary-color); }

.meeting-menu { position: relative; display: inline-block; }
.meeting-menu-pop { position: absolute; background: #fff; border: 1px solid var(--mid-gray); border-radius: 12px; box-shadow: 0 14px 40px rgba(44,41,38,.2); padding: 12px; z-index: 200; width: 260px; }
.meeting-menu-head { font-weight: 800; font-size: 11px; color: var(--font-dark-gray); text-transform: uppercase; letter-spacing: .05em; margin-bottom: 8px; }
.meeting-menu-empty { color: var(--font-mid-gray); font-size: 13px; }
.meeting-menu-pop select { width: 100%; margin-bottom: 10px; }
.meeting-menu-color { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.past-meetings-pop { width: 300px; max-height: 340px; overflow-y: auto; }
.group-edit-pop { width: 220px; }
.group-edit-btn { width: 100%; justify-content: center; margin-bottom: 8px; }
.group-edit-btn:last-child { margin-bottom: 0; }
.meeting-menu-list { display: flex; flex-direction: column; }
.meeting-menu-item { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 9px 10px; border-radius: 8px; cursor: pointer; }
.meeting-menu-item:hover { background: var(--light-gray); }
.meeting-menu-item-name { font-weight: 600; font-size: 13.5px; color: var(--dark-gray); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.meeting-menu-item-date { font-size: 12px; color: var(--font-dark-gray); flex-shrink: 0; }

.template-picker-pop { width: 320px; overflow-y: auto; }
.meeting-template-item { display: flex; align-items: flex-start; gap: 10px; padding: 9px 10px; border-radius: 8px; cursor: pointer; }
.meeting-template-item:hover { background: var(--light-gray); }
.meeting-template-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; margin-top: 4px; }
.meeting-template-dot-custom { background: var(--font-dark-gray); }
.meeting-template-text { min-width: 0; }
.meeting-template-name { font-weight: 700; font-size: 13.5px; color: var(--dark-gray); }
.meeting-template-desc { font-size: 12px; color: var(--font-dark-gray); margin-top: 2px; }
.meeting-template-manage { margin-top: 8px; padding-top: 10px; border-top: 1px solid var(--mid-gray); text-align: center; font-weight: 700; font-size: 12.5px; color: var(--primary-color); cursor: pointer; }
.meeting-template-manage:hover { text-decoration: underline; }

/* Meeting tab row */
.meeting-tabbar { background: var(--light-gray); border-bottom: 1px solid var(--mid-gray); padding: 9px 20px; display: flex; align-items: center; gap: 8px; overflow-x: auto; }
.meeting-tab { display: flex; align-items: center; gap: 8px; padding: 7px 13px; border-radius: 9px; font-weight: 700; font-size: 13px; color: var(--font-dark-gray); cursor: pointer; border: 1px solid var(--mid-gray); background: #fff; white-space: nowrap; }
.meeting-tab.active { background: var(--dark-gray); color: #fff; border-color: var(--dark-gray); }
.meeting-tab-badge { font-size: 9.5px; font-weight: 800; color: var(--font-mid-gray); background: var(--light-gray); border-radius: 5px; padding: 2px 6px; text-transform: uppercase; letter-spacing: .04em; }
.meeting-tab.active .meeting-tab-badge { background: rgba(255,255,255,.14); color: var(--mid-gray); }

.meeting-empty-state { color: var(--font-mid-gray); font-size: 14px; padding: 30px 24px; }

/* History tab */
.meeting-history-row { max-width: 700px; display: flex; align-items: center; gap: 18px; background: #fff; border: 1px solid var(--mid-gray); border-radius: 12px; padding: 14px 18px; margin-bottom: 10px; cursor: pointer; }
.meeting-history-row:hover { border-color: var(--primary-color); }
.meeting-history-date { font-weight: 800; font-size: 12.5px; color: var(--primary-color); width: 110px; flex-shrink: 0; }
.meeting-history-main { flex: 1; min-width: 0; }
.meeting-history-title { font-weight: 700; font-size: 15px; color: var(--dark-gray); }
.meeting-history-meta { display: flex; flex-direction: column; gap: 2px; font-size: 12px; color: var(--font-dark-gray); text-align: right; flex-shrink: 0; }
.meeting-history-view { font-weight: 700; font-size: 12.5px; color: var(--primary-color); flex-shrink: 0; white-space: nowrap; }

/* Recap detail */
.meeting-recap-back { display: inline-block; font-weight: 700; font-size: 13px; color: var(--dark-gray); cursor: pointer; margin-bottom: 14px; }
.meeting-recap-back:hover { color: var(--primary-color); }
.meeting-recap-card { max-width: 700px; background: #fff; border: 1px solid var(--mid-gray); border-radius: 14px; padding: 22px 24px; }
.meeting-recap-title { font-weight: 800; font-size: 19px; color: var(--dark-gray); }
.meeting-recap-meta { color: var(--font-dark-gray); font-size: 13px; margin-top: 3px; }
.meeting-recap-label { font-weight: 800; font-size: 11px; letter-spacing: .05em; text-transform: uppercase; color: var(--font-mid-gray); margin: 18px 0 8px; }
.meeting-recap-attendees { font-size: 14px; color: var(--dark-gray); }
.meeting-recap-col { margin-top: 18px; }
.meeting-recap-col-header { display: flex; align-items: center; gap: 8px; font-weight: 800; font-size: 11px; letter-spacing: .05em; text-transform: uppercase; color: var(--font-mid-gray); margin-bottom: 8px; }
.meeting-recap-col-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.meeting-recap-item { font-size: 14px; color: var(--dark-gray); padding: 7px 0; border-bottom: 1px solid var(--light-gray); }
.meeting-recap-item:last-child { border-bottom: none; }
.meeting-recap-item-row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
.meeting-recap-item-assignee { color: var(--font-dark-gray); font-size: 12.5px; flex-shrink: 0; white-space: nowrap; }
.meeting-recap-item-comments { margin-top: 6px; padding-left: 12px; border-left: 2px solid var(--light-gray); }
.meeting-recap-item-comment { font-size: 12.5px; color: var(--font-dark-gray); margin-top: 4px; }
.meeting-recap-item-comment:first-child { margin-top: 0; }
.meeting-recap-item-comment-author { font-weight: 700; color: var(--dark-gray); }
.meeting-recap-empty { color: var(--font-mid-gray); font-size: 14px; margin-top: 14px; }

/* Meeting header */
.meeting-head { background: #fff; border-bottom: 1px solid var(--mid-gray); padding: 16px 24px; }
.meeting-past-banner { display: flex; align-items: center; gap: 10px; background: var(--light-gray); color: var(--font-dark-gray); font-size: 12.5px; font-weight: 600; border-radius: 8px; padding: 8px 12px; margin-bottom: 14px; }
.meeting-past-banner-back { color: var(--primary-color); font-weight: 700; cursor: pointer; }
.meeting-past-banner-back:hover { text-decoration: underline; }
.meeting-head-top { display: flex; align-items: flex-start; gap: 18px; }
.meeting-bigdate { background: var(--dark-gray); color: #fff; border-radius: 12px; padding: 8px 16px; text-align: center; min-width: 78px; flex-shrink: 0; }
.meeting-bigdate-dow { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #c9c5c0; }
.meeting-bigdate-md { font-size: 20px; font-weight: 800; line-height: 1.1; }
.meeting-head-title { flex: 1; min-width: 0; }
.meeting-head-name { font-weight: 800; font-size: 20px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.meeting-name-input { width: auto; border: none; background: transparent; padding: 0; margin: 0; font: inherit; font-weight: inherit; font-size: inherit; color: var(--dark-gray); field-sizing: content; min-width: 40px; }
.meeting-head-meta { font-size: 13px; color: var(--font-dark-gray); margin-top: 5px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.meeting-recur { font-weight: 700; color: #54565B; }
.meeting-time { font-weight: 600; color: #54565B; }
.meeting-time-none { font-weight: 400; font-style: italic; color: var(--font-mid-gray); }
.meeting-uplbl { font-weight: 700; text-transform: uppercase; font-size: 10px; letter-spacing: .05em; color: var(--font-mid-gray); }
.meeting-upchip { padding: 2px 9px; border-radius: 20px; background: #EFEDEA; color: #54565B; font-weight: 600; font-size: 12px; }

.meeting-private-pill { display: inline-flex; align-items: center; font-weight: 700; font-size: 10.5px; letter-spacing: .04em; text-transform: uppercase; color: var(--primary-color); background: #FCEBEC; border: 1px solid #F4D3D5; border-radius: 20px; padding: 3px 10px; }
.meeting-team-pill { display: inline-flex; align-items: center; font-weight: 700; font-size: 10.5px; letter-spacing: .04em; text-transform: uppercase; background: #F4F2EF; border: 1px solid var(--mid-gray); border-radius: 20px; padding: 3px 10px; }

.meeting-avatars { display: inline-flex; align-items: center; }
.meeting-avatar { width: 26px; height: 26px; border-radius: 50%; background: #024E73; color: #fff; font-weight: 700; font-size: 10px; display: flex; align-items: center; justify-content: center; border: 2px solid #fff; margin-left: -6px; }
.meeting-avatar-more { background: var(--font-mid-gray); font-size: 9px; }
.meeting-avatars .meeting-avatar:first-child { margin-left: 0; }

.meeting-length-box { display: inline-flex; align-items: center; gap: 10px; background: #fff; border: 1px solid var(--mid-gray); border-radius: 10px; padding: 6px 6px 6px 14px; flex-shrink: 0; }
.meeting-total-length { font-weight: 800; font-size: 16px; font-variant-numeric: tabular-nums; color: var(--dark-gray); min-width: 50px; text-align: center; }
.meeting-btn-run { background: var(--primary-color); color: #fff; border: 1px solid var(--primary-color); }
.meeting-btn-run:hover { opacity: .9; }
.meeting-btn-forge { display: inline-flex; align-items: center; gap: 6px; background: linear-gradient(135deg, #f59a2f, #e8661c); color: #fff; border: 1px solid transparent; }
.meeting-btn-forge:hover { opacity: .9; }

.meeting-head-actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 14px; flex-wrap: wrap; }
.meeting-subtabs { display: inline-flex; background: #EFEDEA; border-radius: 9px; padding: 3px; }
.meeting-subtabs button { border: none; background: transparent; color: var(--font-dark-gray); font-weight: 700; font-size: 13px; padding: 7px 16px; border-radius: 7px; cursor: pointer; font-family: inherit; }
.meeting-subtabs button.on { background: #fff; color: var(--dark-gray); box-shadow: 0 1px 3px rgba(0,0,0,.08); }
.meeting-head-btns { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.meeting-share-hint { margin: 0; font-size: 13px; color: var(--font-dark-gray); line-height: 1.45; }
.meeting-share-actions { display: flex; align-items: center; justify-content: flex-end; gap: 16px; margin-top: 24px; }
.meeting-share-actions .btn { margin: 0; padding: 8px 18px; }
.meeting-share-preview { font-size: 13.5px; font-weight: 600; color: var(--font-dark-gray); text-decoration: underline; cursor: pointer; }
.meeting-share-preview:hover { color: var(--primary-color); }

/* Shared button look for this page */
.meeting-btn { border-radius: 6px; padding: 7px 13px; font-size: 13px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-family: inherit; }
.meeting-btn-light { background: #fff; color: var(--dark-gray); border: 1px solid var(--mid-gray); }
.meeting-btn-light:hover { border-color: var(--font-mid-gray); }
.meeting-btn-danger { color: var(--primary-color); }

/* Board wrap */
.meeting-wrap { padding: 16px 20px 22px; }


.meeting-card { background: #fff; border: 1px solid var(--mid-gray); border-radius: 11px; padding: 11px 12px; box-shadow: 0 1px 2px rgba(44,41,38,.04); cursor: pointer; }
.meeting-card-title { font-size: 13.5px; font-weight: 600; line-height: 1.4; color: var(--dark-gray); display: flex; align-items: flex-start; }
.meeting-card-title input[type=checkbox] { width: 15px; height: 15px; margin: 2px 7px 0 0; flex-shrink: 0; cursor: pointer; }
.meeting-card-done .meeting-card-title { text-decoration: line-through; color: var(--font-mid-gray); }
.meeting-due { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; border-radius: 5px; padding: 2px 7px; color: #9A6B15; background: #FBF0DA; }
.meeting-due svg { width: 12px; height: 12px; margin: 0; fill: currentColor; }
.meeting-due.overdue { color: var(--primary-color); background: #FCEBEC; }
.meeting-card-footer { display: flex; align-items: center; gap: 7px; margin-top: 5px; flex-wrap: wrap; }
.meeting-pri { font-size: 10px; font-weight: 800; border-radius: 5px; padding: 2px 7px; text-transform: uppercase; letter-spacing: .03em; }
.meeting-pri.high { background: #FCEBEC; color: var(--primary-color); }
.meeting-pri.med { background: #FBF0DA; color: #9A6B15; }
.meeting-pri.low { background: #EDECEA; color: var(--font-dark-gray); }
.meeting-private { display: inline-flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 800; border-radius: 5px; padding: 2px 7px; text-transform: uppercase; letter-spacing: .03em; background: #E6EEF2; color: #024E73; vertical-align: middle; }
.meeting-recap-item-name .meeting-private { margin-left: 8px; }
.meeting-private-icon { width: 10px; height: 10px; }
.meeting-on-tasks { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; color: var(--font-mid-gray); min-width: 0; }
.meeting-on-tasks svg { width: 14px; height: 14px; margin: 0; fill: var(--dark-gray); }
.meeting-owner { margin-left: auto; width: 24px; height: 24px; border-radius: 50%; background: #024E73; color: #fff; font-weight: 700; font-size: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

/* Run Meeting - full screen, covers the group/tab/page bars */
.run-meeting-overlay { position: fixed; inset: 0; z-index: 99999; background: var(--light-gray); overflow-y: auto; display: flex; flex-direction: column; }
.run-meeting-overlay .meeting-wrap { flex: 1; }
/* A section's team scorecard / rocks, in place of the board while it's on (a divider between the two when both show) */
.run-meeting-panel { margin-bottom: 22px; }
.run-meeting-panel + .run-meeting-panel { padding-top: 20px; border-top: 1px solid var(--mid-gray); }
.run-meeting-panel-h { font-family: var(--head-font); font-weight: 700; font-size: 16px; color: var(--dark-gray); margin-bottom: 12px; }

/* End of Meeting pop-up (End meeting in the run bar): new to-dos and the rating */
.run-end { background: #fff; border: 1px solid var(--mid-gray); border-radius: 14px; width: 640px; max-width: 100%; max-height: 95vh; overflow-y: auto; }
.run-end-head { position: relative; padding: 18px 50px 14px 22px; border-bottom: 1px solid var(--mid-gray); }
.run-end-x { position: absolute; top: 20px; right: 20px; display: flex; cursor: pointer; }
.run-end-x svg { width: 14px; height: 14px; fill: var(--font-mid-gray); }
.run-end-x:hover svg { fill: var(--primary-color); }
.run-end-title { font-family: var(--head-font); font-weight: 700; font-size: 20px; color: var(--dark-gray); }
.run-end-meta { font-size: 12.5px; color: var(--font-mid-gray); margin-top: 2px; }
.run-end-body { padding: 4px 22px 18px; }
.run-end-h { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin: 16px 0 9px; font-weight: 700; font-size: 15px; color: var(--dark-gray); }
.run-end-h span { font-weight: normal; font-size: 12px; color: var(--font-mid-gray); }
.run-end-todos { border: 1px solid var(--mid-gray); border-radius: 10px; overflow: hidden; }
.run-end-todo { display: flex; align-items: center; gap: 14px; padding: 10px 12px; font-size: 13.5px; cursor: pointer; }
.run-end-todo + .run-end-todo { border-top: 1px solid var(--mid-gray); }
.run-end-todo:hover { background: var(--light-gray); }
.run-end-todo-name { flex: 1; min-width: 0; color: var(--font-dark-gray); overflow-wrap: anywhere; }
.run-end-todo b { font-size: 12px; color: var(--dark-gray); white-space: nowrap; }
.run-end-todo-due { font-size: 12px; color: var(--font-mid-gray); white-space: nowrap; min-width: 30px; text-align: right; }
.run-end-rates { display: grid; grid-template-columns: repeat(10, 1fr); gap: 6px; }
.run-end-rate { aspect-ratio: 1; max-height: 44px; border: 1px solid var(--font-mid-gray); border-radius: 8px; background: #fff; color: var(--dark-gray); font-family: inherit; font-weight: 700; font-size: 15px; cursor: pointer; padding: 0; }
.run-end-rate:hover { border-color: var(--dark-gray); }
.run-end-rate.on { background: #2E7D46; border-color: #2E7D46; color: #fff; }
.run-end-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 22px; border-top: 1px solid var(--mid-gray); }
.run-end-last { font-size: 12.5px; color: var(--font-mid-gray); }
.run-end-ask { font-weight: 700; font-size: 13.5px; color: var(--dark-gray); }
.run-end-btns { display: flex; gap: 10px; margin-left: auto; }
.run-end-btn { margin: 0; padding: 9px 20px; }
@media (max-width: 600px) {
    .run-end-rates { grid-template-columns: repeat(5, 1fr); }
    .run-end-rate { aspect-ratio: auto; height: 40px; max-height: none; }
}

.run-meeting-bar { background: var(--dark-gray); color: #fff; padding: 13px 22px; display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap; }
.run-meeting-bar-left { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; }
/* The recording box always takes its own full-width row, between the timers and the buttons, so it doesn't jump around as
   its content changes */
.run-meeting-recorder { flex: 0 0 100%; box-sizing: border-box; }
.run-meeting-title { font-weight: 800; font-size: 17px; }
.run-meeting-stat { text-align: center; line-height: 1; }
.run-meeting-stat-time { font-weight: 800; font-size: 26px; font-variant-numeric: tabular-nums; }
.run-meeting-stat-time.over { color: #F0603A; }
.run-meeting-stat-label { font-size: 10px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: #9a958f; margin-top: 5px; }
.run-meeting-desc-name { font-weight: 700; font-size: 15px; }
.run-meeting-desc-text { font-size: 13px; color: #b5b0aa; }
/* The run buttons always get their own row under the timers (and the recording box) */
.run-meeting-bar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; flex: 0 0 100%; }

.run-meeting-btn { display: inline-flex; align-items: center; gap: 7px; background: rgba(255,255,255,.14); color: #fff; border: none; border-radius: 8px; padding: 9px 14px; font-weight: 700; font-size: 13px; cursor: pointer; font-family: inherit; }
.run-meeting-btn:hover { opacity: .85; }
.run-meeting-btn-attend { background: #F0742A; }
.run-meeting-btn-end { background: var(--primary-color); }

.run-meeting-sections { background: var(--dark-gray); padding: 16px 22px; display: flex; gap: 12px; overflow-x: auto; flex-shrink: 0; border-top: 1px solid rgba(255,255,255,.08); }
.run-meeting-section-card { background: var(--dark-gray); border: 2px solid rgba(255,255,255,.15); border-radius: 12px; padding: 12px 16px; min-width: 150px; cursor: pointer; flex-shrink: 0; }
/* Equal widths filling the strip; min-width (150px, above) still makes a long agenda scroll */
.run-meeting-sections-fill .run-meeting-section-card { flex: 1 1 0; }
.run-meeting-section-card:hover { border-color: rgba(255,255,255,.35); }
.run-meeting-section-card.active { border-color: #fff; }
.run-meeting-section-top { display: flex; align-items: center; gap: 9px; margin-bottom: 8px; }
.run-meeting-section-num { width: 22px; height: 22px; border-radius: 50%; background: rgba(255,255,255,.15); color: #fff; font-weight: 800; font-size: 11px; display: flex; align-items: center; justify-content: center; }
.run-meeting-section-card.active .run-meeting-section-num { background: #fff; color: var(--dark-gray); }
.run-meeting-section-min { font-size: 12px; color: #b5b0aa; font-weight: 700; }
.run-meeting-section-name { font-weight: 700; font-size: 14px; color: #fff; }

@media (max-width: 767px) {
    .meeting-head { padding: 14px 16px; }
    /* Letting the row wrap (instead of squeezing the name/pills/avatars into whatever
       sliver is left beside the date badge and length box) pushes the length box onto
       its own line, since the flex:1 title always claims the rest of the first line first */
    .meeting-head-top { flex-wrap: wrap; row-gap: 12px; }
    .meeting-length-box { width: 100%; justify-content: space-between; }
}
</style>

<style scoped>
.meeting-header-actions { display:flex; flex-direction:column; align-items:flex-end; gap:5px; }
.meeting-history-links { display:flex; flex-direction:column; align-items:flex-end; gap:8px; }
@media(max-width:700px) { .meeting-history-row { flex-wrap:wrap; } .meeting-history-links { align-items:flex-start; } }
</style>
